
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { handleHybridPdfDownload } from '../utils/downloadUtils';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Header from '../components/Layout/Header';
import { Avatar } from '../utils/avatar';
import AssignModal from '../components/AssignModal';
import AICompareModal from '../components/AICompareModal';
import HistorySidebar from '../components/HistorySidebar';
import { formatDate, formatRelativeTime } from '../utils/dateFormat';
import RupiahInput from '../components/RupiahInput';
import { formatRupiah } from '../utils/currency';
import { replaceVariablesInHTML } from '../utils/docxParser';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import { appendAttachmentsToZip } from '../utils/docxAttachmentAppender';
import { API_BASE_URL } from '../config';

// Helper function to handle document download/view
const handleDocumentAction = async (filePath: string, fileName: string, isPdf: boolean) => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    alert('Anda harus login untuk mengakses dokumen');
    return;
  }

  // Extract just the filename from path (remove 'uploads/documents/' prefix if exists)
  let cleanPath = filePath;
  if (cleanPath.startsWith('uploads/documents/')) {
    cleanPath = cleanPath.replace('uploads/documents/', '');
  }

  const url = `${API_BASE_URL}/api/documents/download/${cleanPath}`;

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        alert('Sesi Anda telah berakhir. Silakan login kembali.');
        return;
      }
      throw new Error(`Failed to fetch document: ${response.status} ${response.statusText} `);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    if (isPdf) {
      // Open PDF in new tab using a temporary link element
      // This approach is more reliable and won't affect the current page
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke URL after a delay to allow the new window to load
      // Keep the blob URL alive longer for PDF viewing
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 2000);
    } else {
      // Download other files
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    }
  } catch (error) {
    console.error('Error handling document:', error);
    alert('Gagal membuka/mengunduh dokumen. Silakan coba lagi.');
  }
};

interface DragDropFileUploadProps {
  files: File[];
  setFiles: (files: File[]) => void;
  darkMode: boolean;
  id: string;
}

const DragDropFileUpload: React.FC<DragDropFileUploadProps> = ({ files, setFiles, darkMode, id }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter(file => file.type === 'application/pdf');
      if (droppedFiles.length > 0) {
        setFiles([...files, ...droppedFiles]);
      }
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).filter(file => file.type === 'application/pdf');
      if (selectedFiles.length > 0) {
        setFiles([...files, ...selectedFiles]);
      }
    }
    // reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging 
            ? (darkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50') 
            : (darkMode ? 'border-gray-700 hover:border-gray-600 bg-gray-800' : 'border-gray-300 hover:border-gray-400 bg-gray-50')
        }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          id={id}
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={onFileChange}
        />
        <i className={`fa-solid fa-cloud-arrow-up text-3xl mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
        <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Drag & Drop file PDF di sini atau klik untuk memilih
        </p>
        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Mendukung multiple upload (.pdf)
        </p>
      </div>
      
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div key={index} className={`flex items-center justify-between p-2 rounded-md ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'} text-xs`}>
              <div className="flex items-center overflow-hidden">
                <i className="fa-solid fa-file-pdf text-red-500 mr-2 flex-shrink-0"></i>
                <span className={`truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} title={file.name}>
                  {file.name}
                </span>
                <span className={`ml-2 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                className={`ml-2 hover:text-red-500 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBusinessUser = user?.name?.toLowerCase().includes('business') || user?.email?.toLowerCase().includes('business');
  const isLegalUser = user?.name?.toLowerCase().includes('legal') || user?.email?.toLowerCase().includes('legal');
  const { darkMode } = useTheme();
  const [task, setTask] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [logsSkip, setLogsSkip] = useState(0);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [commentMentions, setCommentMentions] = useState<Array<{ user_id?: number; group_id?: number; mention_type: string }>>([]);
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editTaskData, setEditTaskData] = useState<any>(null);
  const [savingTask, setSavingTask] = useState(false);
  const [variablesExpanded, setVariablesExpanded] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignMentions, setAssignMentions] = useState<Array<{ user_id?: number; group_id?: number; mention_type: string }>>([]);
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: '',
    comment: ''
  });
  const [commentMentionSearch, setCommentMentionSearch] = useState('');
  const [showCommentMentionDropdown, setShowCommentMentionDropdown] = useState(false);
  const [commentMentionPosition, setCommentMentionPosition] = useState<number>(0);
  const [supportingDocuments, setSupportingDocuments] = useState<File[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [uploadDocForm, setUploadDocForm] = useState({
    file: null as File | null,
    type: 'new' as 'new' | 'update',
    reason: '',
    changes: '',
    updated_from: ''
  });

  const [releasedStatusId, setReleasedStatusId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [documentVersions, setDocumentVersions] = useState<any[]>([]);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [uploadVersionFile, setUploadVersionFile] = useState<File | null>(null);
  const [uploadNote, setUploadNote] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // AI Document Review State
  const [aiReviewFiles, setAiReviewFiles] = useState<File[]>([]);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewPolling, setAiReviewPolling] = useState(false);
  const [aiReviewResult, setAiReviewResult] = useState<any>(null);
  const [aiReviewComparisonId, setAiReviewComparisonId] = useState<number | null>(null);
  const [aiReviewStatus, setAiReviewStatus] = useState<string>('');
  const [aiReviewExpanded, setAiReviewExpanded] = useState(false);
  const [aiReviewActiveTab, setAiReviewActiveTab] = useState<'summary' | 'categories' | 'findings' | 'recommendations'>('summary');
  const [aiReviewLanguage, setAiReviewLanguage] = useState<'indonesia' | 'inggris'>('indonesia');

  // AI Risk Assessment State (agent_risk)
  const [aiRiskFiles, setAiRiskFiles] = useState<File[]>([]);
  const [aiRiskLoading, setAiRiskLoading] = useState(false);
  const [aiRiskPolling, setAiRiskPolling] = useState(false);
  const [aiRiskResult, setAiRiskResult] = useState<any>(null);
  const [aiRiskComparisonId, setAiRiskComparisonId] = useState<number | null>(null);
  const [aiRiskStatus, setAiRiskStatus] = useState<string>('');
  const [aiRiskExpanded, setAiRiskExpanded] = useState(false);
  const [aiRiskActiveTab, setAiRiskActiveTab] = useState<'summary' | 'register' | 'categories' | 'recommendations'>('summary');
  const [aiRiskLanguage, setAiRiskLanguage] = useState<'indonesia' | 'inggris'>('indonesia');

  // Review Comments State
  const [reviewComments, setReviewComments] = useState<any[]>([]);
  const [showReviewComments, setShowReviewComments] = useState(false);

  // AI Comparison State
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [loadingReviewComments, setLoadingReviewComments] = useState(false);
  const [newReviewComment, setNewReviewComment] = useState('');
  const [submittingReviewComment, setSubmittingReviewComment] = useState(false);
  const [replyingToCommentId, setReplyingToCommentId] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Assign Modal State
  const [showReviewAssignModal, setShowReviewAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // History Sidebar State
  // const [showHistorySidebar, setShowHistorySidebar] = useState(false); // Removed, using historyOpen instead

  // Load task, comments, and logs
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const [taskData, commentsData, logsData, usersData, groupsData, assignmentsData, statusesData, versionsData, reviewCommentsData] = await Promise.all([
          apiService.getTask(Number(id)),
          apiService.getTaskComments(Number(id)),
          apiService.getTaskLogs(Number(id), { skip: 0, limit: 100 }), // Increase limit for history
          apiService.getUsers(),
          apiService.getGroups(),
          apiService.getTaskAssignments(Number(id)),
          apiService.getStatuses(),
          apiService.getTaskVersions(Number(id)).catch(err => {
            console.warn('Failed to fetch task versions:', err);
            return [];
          }),
          apiService.getReviewComments(Number(id)) // Eager load review comments
        ]);

        const releasedStatus = statusesData.find((s: any) => s.name.toUpperCase() === 'RELEASED');
        if (releasedStatus) {
          setReleasedStatusId(releasedStatus.id);
        }
        setStatuses(statusesData);
        setDocumentVersions(versionsData);

        setTask(taskData);
        setComments(commentsData);
        setReviewComments(reviewCommentsData); // Set eager loaded review comments
        setLogs(logsData);
        setLogsSkip(5);
        setLogsHasMore(logsData.length === 5); // If we got 5, there might be more
        setUsers(usersData);
        setGroups(groupsData);
        setAssignments(assignmentsData);
        // Initialize assign mentions from task mentions
        if (taskData.mentions) {
          setAssignMentions(taskData.mentions.map((m: any) => ({
            user_id: m.user_id,
            group_id: m.group_id,
            mention_type: m.mention_type
          })));
        }
        // Initialize assign mentions from task mentions
        if (taskData.mentions) {
          setAssignMentions(taskData.mentions.map((m: any) => ({
            user_id: m.user_id,
            group_id: m.group_id,
            mention_type: m.mention_type
          })));
        }
      } catch (err: any) {
        console.error('Error loading task data:', err);
        setError('Gagal memuat data task');
      } finally {
        setLoading(false);
      }
    };



    loadData();

    // Auto-refresh Review Comments every 15 seconds
    const intervalId = setInterval(() => {
      if (id) {
        loadReviewComments(true);
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [id]);

  // Load existing AI Review result on mount
  useEffect(() => {
    const loadExistingAIReview = async () => {
      if (!id) return;
      try {
        const comparison = await apiService.getTaskAIComparisonByAgent(Number(id), 'agent_review');
        if (comparison) {
          setAiReviewComparisonId(comparison.id);
          setAiReviewStatus(comparison.status);
          if (comparison.status === 'COMPLETED' && comparison.issues) {
            // issues field is used to store the full review result for agent_review
            const resultData = Array.isArray(comparison.issues) ? comparison.issues[0] : comparison.issues;
            if (resultData) setAiReviewResult(resultData);
          } else if (comparison.status === 'PENDING' || comparison.status === 'PROCESSING') {
            // Start polling if still processing
            pollAiReviewStatus(comparison.id);
          }
        }
      } catch (err) {
        // No previous review found, that's ok
      }
    };
    loadExistingAIReview();
  }, [id]);

  // Poll AI review status
  const pollAiReviewStatus = async (comparisonId: number) => {
    setAiReviewPolling(true);
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setAiReviewPolling(false);
        setAiReviewStatus('FAILED');
        return;
      }
      attempts++;
      try {
        const status = await apiService.getAIAnalysisStatus(comparisonId);
        setAiReviewStatus(status.status);
        if (status.status === 'COMPLETED') {
          setAiReviewPolling(false);
          setAiReviewLoading(false);
          if (status.issues) {
            const resultData = Array.isArray(status.issues) ? status.issues[0] : status.issues;
            if (resultData) setAiReviewResult(resultData);
          }
        } else if (status.status === 'FAILED') {
          setAiReviewPolling(false);
          setAiReviewLoading(false);
        } else {
          // Still processing, poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err) {
        setAiReviewPolling(false);
        setAiReviewLoading(false);
      }
    };

    poll();
  };

  // Handle run AI review
  const handleRunAiReview = async () => {
    if (aiReviewFiles.length === 0) {
      alert('Silakan pilih dokumen terlebih dahulu');
      return;
    }

    setAiReviewLoading(true);
    setAiReviewResult(null);
    setAiReviewStatus('PENDING');

    try {
      const response = await apiService.analyzeWithAI(aiReviewFiles, null, Number(id), 'agent_review', aiReviewLanguage);
      const compId = response.comparison_id;
      setAiReviewComparisonId(compId);
      setAiReviewStatus('PENDING');
      pollAiReviewStatus(compId);
    } catch (err: any) {
      console.error('AI Review error:', err);
      setAiReviewLoading(false);
      setAiReviewStatus('FAILED');
    }
  };

  // Load existing AI Risk result on mount
  useEffect(() => {
    const loadExistingAIRisk = async () => {
      if (!id) return;
      try {
        const comparison = await apiService.getTaskAIComparisonByAgent(Number(id), 'agent_risk');
        if (comparison) {
          setAiRiskComparisonId(comparison.id);
          setAiRiskStatus(comparison.status);
          if (comparison.status === 'COMPLETED' && comparison.issues) {
            const resultData = Array.isArray(comparison.issues) ? comparison.issues[0] : comparison.issues;
            if (resultData) setAiRiskResult(resultData);
          } else if (comparison.status === 'PENDING' || comparison.status === 'PROCESSING') {
            pollAiRiskStatus(comparison.id);
          }
        }
      } catch (err) {
        // No previous risk review found
      }
    };
    loadExistingAIRisk();
  }, [id]);

  // Poll AI risk status
  const pollAiRiskStatus = async (comparisonId: number) => {
    setAiRiskPolling(true);
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setAiRiskPolling(false);
        setAiRiskStatus('FAILED');
        return;
      }
      attempts++;
      try {
        const status = await apiService.getAIAnalysisStatus(comparisonId);
        setAiRiskStatus(status.status);
        if (status.status === 'COMPLETED') {
          setAiRiskPolling(false);
          setAiRiskLoading(false);
          if (status.issues) {
            const resultData = Array.isArray(status.issues) ? status.issues[0] : status.issues;
            if (resultData) setAiRiskResult(resultData);
          }
        } else if (status.status === 'FAILED') {
          setAiRiskPolling(false);
          setAiRiskLoading(false);
        } else {
          setTimeout(poll, 5000);
        }
      } catch (err) {
        setAiRiskPolling(false);
        setAiRiskLoading(false);
      }
    };

    poll();
  };

  // Handle run AI risk
  const handleRunAiRisk = async () => {
    if (aiRiskFiles.length === 0) {
      alert('Silakan pilih dokumen terlebih dahulu');
      return;
    }

    setAiRiskLoading(true);
    setAiRiskResult(null);
    setAiRiskStatus('PENDING');

    try {
      const response = await apiService.analyzeWithAI(aiRiskFiles, null, Number(id), 'agent_risk', aiRiskLanguage);
      const compId = response.comparison_id;
      setAiRiskComparisonId(compId);
      setAiRiskStatus('PENDING');
      pollAiRiskStatus(compId);
    } catch (err: any) {
      console.error('AI Risk error:', err);
      setAiRiskLoading(false);
      setAiRiskStatus('FAILED');
    }
  };


  // Helper: get compliance status color
  const getComplianceStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'compliant': return darkMode ? 'bg-green-900/30 text-green-300 border-green-700' : 'bg-green-100 text-green-700 border-green-300';
      case 'needs improvement': return darkMode ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'non-compliant': return darkMode ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-red-100 text-red-700 border-red-300';
      case 'missing': return darkMode ? 'bg-gray-700/50 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-300';
      default: return darkMode ? 'bg-gray-700/50 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700';
      case 'high': return darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700';
      case 'medium': return darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700';
      case 'low': return darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
      default: return darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-600';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case 'critical risk': case 'critical': return '#ef4444';
      case 'high risk': case 'high': return '#f97316';
      case 'moderate risk': case 'medium': return '#eab308';
      case 'low risk': case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const getRiskLevelBadge = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'critical': return darkMode ? 'bg-red-900/50 text-red-300 border-red-700' : 'bg-red-100 text-red-700 border-red-300';
      case 'high': return darkMode ? 'bg-orange-900/50 text-orange-300 border-orange-700' : 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return darkMode ? 'bg-yellow-900/50 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return darkMode ? 'bg-green-900/50 text-green-300 border-green-700' : 'bg-green-100 text-green-700 border-green-300';
      default: return darkMode ? 'bg-gray-700/50 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  // Load review comments
  const loadReviewComments = async (isBackground = false) => {
    if (!id) return;

    if (!isBackground) {
      setLoadingReviewComments(true);
    }
    try {
      const reviewCommentsData = await apiService.getReviewComments(Number(id));
      setReviewComments(reviewCommentsData);
    } catch (err: any) {
      console.error('Error loading review comments:', err);
    } finally {
      if (!isBackground) {
        setLoadingReviewComments(false);
      }
    }
  };

  // Load review comments when panel is opened
  useEffect(() => {
    if (showReviewComments && reviewComments.length === 0) {
      loadReviewComments();
    }
  }, [showReviewComments]);

  // Handle comment submission
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !id) return;

    setSubmittingComment(true);
    try {
      const comment = await apiService.createTaskComment(Number(id), newComment, commentMentions);
      setComments([comment, ...comments]);
      setNewComment('');
      setCommentMentions([]);
    } catch (err: any) {
      console.error('Error creating comment:', err);
      setError('Gagal menambahkan komentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle comment update
  const handleUpdateComment = async (commentId: number) => {
    if (!editCommentContent.trim() || !id) return;

    try {
      const updatedComment = await apiService.updateTaskComment(Number(id), commentId, editCommentContent, commentMentions);
      setComments(comments.map(c => c.id === commentId ? updatedComment : c));
      setEditingCommentId(null);
      setEditCommentContent('');
      setCommentMentions([]);
    } catch (err: any) {
      console.error('Error updating comment:', err);
      setError('Gagal mengupdate komentar');
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId: number) => {
    if (!id || !window.confirm('Apakah Anda yakin ingin menghapus komentar ini?')) return;

    try {
      await apiService.deleteTaskComment(Number(id), commentId);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      setError('Gagal menghapus komentar');
    }
  };

  // Start editing comment
  const startEditingComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
    if (comment.mentions) {
      setCommentMentions(comment.mentions.map((m: any) => ({
        user_id: m.user_id,
        group_id: m.group_id,
        mention_type: m.mention_type
      })));
    } else {
      setCommentMentions([]);
    }
  };

  // Load more logs
  const handleLoadMoreLogs = async () => {
    if (!id || loadingMoreLogs) return;

    setLoadingMoreLogs(true);
    try {
      const moreLogs = await apiService.getTaskLogs(Number(id), { skip: logsSkip, limit: 5 });
      if (moreLogs.length > 0) {
        setLogs([...logs, ...moreLogs]);
        setLogsSkip(logsSkip + moreLogs.length);
        setLogsHasMore(moreLogs.length === 5); // If we got 5, there might be more
      } else {
        setLogsHasMore(false);
      }
    } catch (err: any) {
      console.error('Error loading more logs:', err);
      setError('Gagal memuat lebih banyak log');
    } finally {
      setLoadingMoreLogs(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditCommentContent('');
    setCommentMentions([]);
  };

  // Review Comment Handlers
  const handleCreateReviewComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReviewComment.trim() || !id) return;

    setSubmittingReviewComment(true);
    try {
      const comment = await apiService.createReviewComment(
        Number(id),
        newReviewComment,
        undefined, // selectedText
        undefined, // startOffset
        undefined, // endOffset
        [], // mentions
        undefined // parentCommentId
      );
      setReviewComments([comment, ...reviewComments]);
      setNewReviewComment('');
    } catch (err: any) {
      console.error('Error creating review comment:', err);
      setError('Gagal menambahkan review comment');
    } finally {
      setSubmittingReviewComment(false);
    }
  };

  const handleReplyToReviewComment = async (commentId: number) => {
    if (!replyContent.trim() || !id) return;

    setSubmittingReply(true);
    try {
      const reply = await apiService.replyToReviewComment(
        Number(id),
        commentId,
        replyContent,
        []
      );
      // Reload review comments to get updated thread
      await loadReviewComments();
      setReplyingToCommentId(null);
      setReplyContent('');
    } catch (err: any) {
      console.error('Error replying to review comment:', err);
      setError('Gagal menambahkan reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleFlagReviewCommentAsDone = async (commentId: number) => {
    if (!id) return;

    try {
      await apiService.flagReviewCommentAsDone(Number(id), commentId);
      // Update local state
      setReviewComments(reviewComments.map(c =>
        c.id === commentId ? { ...c, is_resolved: true } : c
      ));
    } catch (err: any) {
      console.error('Error flagging comment as done:', err);
      setError('Gagal menandai comment sebagai selesai');
    }
  };

  const handleUnflagReviewCommentAsDone = async (commentId: number) => {
    if (!id) return;

    try {
      await apiService.unflagReviewCommentAsDone(Number(id), commentId);
      // Update local state
      setReviewComments(reviewComments.map(c =>
        c.id === commentId ? { ...c, is_resolved: false } : c
      ));
    } catch (err: any) {
      console.error('Error unflagging comment:', err);
      setError('Gagal menghapus tanda selesai');
    }
  };

  // Handle task edit mode
  const handleEditTask = () => {
    setIsEditingTask(true);
    setEditTaskData({
      title: task.title,
      description: task.description || ''
    });
    // Reset supporting documents for new uploads
    setSupportingDocuments([]);
  };

  // Handle save task
  const handleSaveTask = async () => {
    if (!id || !editTaskData) return;

    setSavingTask(true);
    setUploadingDocuments(true);
    try {
      // Upload supporting documents first if any
      const uploadedDocuments: any[] = [];
      if (supportingDocuments.length > 0) {
        for (const file of supportingDocuments) {
          try {
            const uploadResult = await apiService.saveDocument(file);
            uploadedDocuments.push({
              file_path: uploadResult.file_path,
              file_name: uploadResult.file_name,
              saved_name: uploadResult.saved_name,
              size: file.size
            });
          } catch (err: any) {
            console.error(`Error uploading file ${file.name}: `, err);
            setError(`Gagal mengupload file ${file.name}. Silakan coba lagi.`);
            setSavingTask(false);
            setUploadingDocuments(false);
            return;
          }
        }
      }

      // Merge with existing supporting documents
      const existingDocs = task.supporting_documents && Array.isArray(task.supporting_documents)
        ? task.supporting_documents
        : [];
      const allDocuments = [...existingDocs, ...uploadedDocuments];

      // Update task with new data and supporting documents
      const updateData: any = {
        ...editTaskData
      };

      // Always include supporting_documents to ensure it's saved to database
      // Merge existing documents with newly uploaded ones
      updateData.supporting_documents = allDocuments.length > 0 ? allDocuments : (existingDocs.length > 0 ? existingDocs : []);

      console.log('Updating task with data:', updateData);
      console.log('Supporting documents to save:', updateData.supporting_documents);
      const updatedTask = await apiService.updateTask(Number(id), updateData);
      setTask(updatedTask);
      setIsEditingTask(false);
      setEditTaskData(null);
      setSupportingDocuments([]);
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError('Gagal mengupdate task');
    } finally {
      setSavingTask(false);
      setUploadingDocuments(false);
    }
  };

  // Handle cancel edit task
  const handleCancelEditTask = () => {
    setIsEditingTask(false);
    setEditTaskData(null);
    setSupportingDocuments([]);
  };

  // Handle submit to review
  const handleSubmitToReview = () => {
    if (!id) return;
    setShowReviewAssignModal(true);
  };

  const handleAssignReviewer = async (userId: number, comment?: string) => {
    if (!id) return;

    setIsAssigning(true);
    try {
      // 1. Create Assignment
      await apiService.createTaskAssignment({
        task_id: Number(id),
        user_id: userId,
        comment: comment
      });

      // 2. Update Status to BUSINESS REVIEW (3)
      const updateData = {
        status_id: 3
      };

      const updatedTask = await apiService.updateTask(Number(id), updateData);
      setTask(updatedTask);
      alert('Task berhasil dikirim ke Business Review dan di-assign ke reviewer!');
      setShowReviewAssignModal(false);
    } catch (err: any) {
      console.error('Error submitting to review:', err);
      setError(err.message || 'Gagal mengirim task ke review. Silakan coba lagi.');
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle Approve Task
  const handleApproveTask = async () => {
    if (!id) return;

    if (!window.confirm('Apakah Anda yakin ingin menyetujui dokumen ini? Status akan berubah menjadi APPROVED.')) {
      return;
    }

    setSavingTask(true);
    try {
      // Update Status to APPROVED (5)
      const updateData = {
        status_id: 5
      };

      const updatedTask = await apiService.updateTask(Number(id), updateData);
      setTask(updatedTask);
      // alert('Task berhasil disetujui!'); // Optional: less intrusive UX prefered usually
    } catch (err: any) {
      console.error('Error approving task:', err);
      setError(err.message || 'Gagal menyetujui task');
    } finally {
      setSavingTask(false);
    }
  };

  const handleGenerateAndRelease = async () => {
    if (!id || !task) return;

    if (!task.template_id) {
      setError("Tidak ada template yang terasosiasi dengan task ini.");
      return;
    }

    setIsGenerating(true);
    try {
      // 1. Fetch template file
      const templateFileResponse = await fetch(`${API_BASE_URL} /api/templates / ${task.template_id}/file`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (!templateFileResponse.ok) throw new Error("Gagal memuat file template");
      const templateBlob = await templateFileResponse.blob();

      // 2. Prepare variables
      const variablesMap: Record<string, string> = {};
      if (task.variables) {
        task.variables.forEach((v: any) => {
          variablesMap[v.name] = v.value || '';
        });
      }

      // Handle agreement_no
      const agreementNoValue = task.agreement_no || '';
      if (agreementNoValue) {
        variablesMap['no_agreement'] = agreementNoValue;
        variablesMap['NO_AGREEMENT'] = agreementNoValue;
        variablesMap['No_Agreement'] = agreementNoValue;
        variablesMap['No_agreement'] = agreementNoValue;
        variablesMap['no'] = agreementNoValue;
        variablesMap['NO'] = agreementNoValue;
        variablesMap['No'] = agreementNoValue;
      }

      // 3. Render Doc
      const zip = new PizZip(await templateBlob.arrayBuffer());
      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });
      doc.render(variablesMap);
      let zipObj = doc.getZip();

      // 4. Handle Attachments
      const attachmentsData = await apiService.getTaskAttachments(Number(id));
      if (attachmentsData && attachmentsData.length > 0) {
        const attachmentImages = [];
        const token = localStorage.getItem('access_token');
        for (const att of attachmentsData) {
          const imageUrl = apiService.getAttachmentImageUrl(att.id);
          try {
            const response = await fetch(imageUrl, { headers: { 'Authorization': `Bearer ${token}` } });
            if (response.ok) {
              const blob = await response.blob();
              attachmentImages.push({
                blob: blob,
                name: `attachment_${att.id}`,
                type: blob.type || 'image/png',
                title: att.title || 'Attachment'
              });
            }
          } catch (e) {
            console.warn(`Failed to fetch attachment ${att.title}`, e);
          }
        }

        if (attachmentImages.length > 0) {
          zipObj = await appendAttachmentsToZip(zipObj, attachmentImages);
        }
      }

      const blob = zipObj.generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
      });

      const fileName = task.template?.file_name || `Document_${task.title}.docx`;
      saveAs(blob, fileName);

      // 5. Update Status to RELEASED
      if (releasedStatusId) {
        await apiService.updateTask(Number(id), { status_id: releasedStatusId });
        const updatedTask = await apiService.getTask(Number(id));
        setTask(updatedTask);
        alert("Dokumen berhasil di-generate dan status task diubah menjadi RELEASED");
      } else {
        alert("Dokumen berhasil di-generate, tetapi gagal mengubah status (Status RELEASED tidak ditemukan)");
      }

    } catch (err: any) {
      console.error("Error generating document:", err);
      setError(err.message || "Gagal generate dokumen");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle upload new supporting document
  const handleUploadDocument = async () => {
    if (!id || !uploadDocForm.file) {
      setError('Silakan pilih file untuk diupload');
      return;
    }

    if (!uploadDocForm.reason) {
      setError('Silakan isi alasan mengupload file ini');
      return;
    }

    if (uploadDocForm.type === 'update' && !uploadDocForm.changes) {
      setError('Silakan isi perubahan yang dilakukan');
      return;
    }

    if (uploadDocForm.type === 'update' && !uploadDocForm.updated_from) {
      setError('Silakan pilih dokumen yang diupdate');
      return;
    }

    setUploadingDocuments(true);
    try {
      // Upload file
      const uploadResult = await apiService.saveDocument(uploadDocForm.file);

      // Prepare document metadata
      const newDoc: any = {
        file_path: uploadResult.file_path,
        file_name: uploadResult.file_name,
        saved_name: uploadResult.saved_name,
        size: uploadDocForm.file.size,
        uploaded_by: {
          id: user?.id,
          name: user?.name,
          email: user?.email
        },
        uploaded_at: new Date().toISOString(),
        type: uploadDocForm.type,
        reason: uploadDocForm.reason,
        changes: uploadDocForm.type === 'update' ? uploadDocForm.changes : null,
        updated_from: uploadDocForm.type === 'update' ? uploadDocForm.updated_from : null
      };

      // Get existing documents
      const existingDocs = task.supporting_documents && Array.isArray(task.supporting_documents)
        ? task.supporting_documents
        : [];

      // Add new document
      const allDocuments = [...existingDocs, newDoc];

      // Update task
      const updatedTask = await apiService.updateTask(Number(id), {
        supporting_documents: allDocuments
      });

      setTask(updatedTask);
      setShowUploadDocModal(false);
      setUploadDocForm({
        file: null,
        type: 'new',
        reason: '',
        changes: '',
        updated_from: ''
      });
      setError('');
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setError('Gagal mengupload dokumen. Silakan coba lagi.');
    } finally {
      setUploadingDocuments(false);
    }
  };

  // Handle upload version
  const handleUploadVersion = async () => {
    if (!uploadVersionFile || !id) return;

    if (!uploadNote.trim()) {
      alert('Mohon isi catatan untuk versi ini');
      return;
    }

    if (!window.confirm(`Upload "${uploadVersionFile.name}" sebagai versi baru?`)) return;

    setUploadingVersion(true);
    try {
      await apiService.uploadTaskVersion(Number(id), uploadVersionFile, uploadNote);

      // Reload versions
      const versions = await apiService.getTaskVersions(Number(id));
      setDocumentVersions(versions);

      // Reset state
      setUploadVersionFile(null);
      setUploadNote('');
      setShowUploadModal(false);

      alert('Versi baru berhasil diupload');
    } catch (err: any) {
      console.error('Error uploading version:', err);
      alert(err.message || 'Gagal mengupload versi baru');
    } finally {
      setUploadingVersion(false);
    }
  };

  // Handle file select for upload modal
  const handleFileSelectForUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadDocForm(prev => ({ ...prev, file: files[0] }));
    }
  };

  // Handle assign mention
  const handleAddAssignMention = (item: any, type: 'user' | 'group') => {
    const mention = {
      user_id: type === 'user' ? item.id : undefined,
      group_id: type === 'group' ? item.id : undefined,
      mention_type: type
    };
    setAssignMentions([...assignMentions, mention]);
    setAssignSearch('');
    setShowAssignDropdown(false);
  };

  // Handle remove assign mention
  const handleRemoveAssignMention = (index: number) => {
    setAssignMentions(assignMentions.filter((_, i) => i !== index));
  };

  // Handle save assign mentions to task
  const handleSaveAssignMentions = async () => {
    if (!id) return;

    try {
      const updatedTask = await apiService.updateTask(Number(id), {
        mentions: assignMentions
      });
      setTask(updatedTask);
      // Update assignMentions from updated task
      if (updatedTask.mentions) {
        setAssignMentions(updatedTask.mentions.map((m: any) => ({
          user_id: m.user_id,
          group_id: m.group_id,
          mention_type: m.mention_type
        })));
      }
      setAssignSearch('');
      setShowAssignDropdown(false);
    } catch (err: any) {
      console.error('Error updating task mentions:', err);
      setError('Gagal mengupdate assign');
    }
  };

  // Handle assignment form submit
  const handleAssignmentSubmit = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }

    if (!assignmentForm.user_id) {
      setError('Silakan pilih user untuk assignment');
      return;
    }

    const assignmentData = {
      task_id: Number(id),
      user_id: Number(assignmentForm.user_id),
      comment: assignmentForm.comment || undefined
    };

    console.log('Creating assignment with data:', assignmentData);

    setSubmittingAssignment(true);
    try {
      const result = await apiService.createTaskAssignment(assignmentData);
      console.log('Assignment created successfully:', result);

      // Reload assignments immediately
      const assignmentsData = await apiService.getTaskAssignments(Number(id));
      console.log('Reloaded assignments:', assignmentsData);
      setAssignments(assignmentsData);

      // Reset form
      setAssignmentForm({
        user_id: '',
        comment: ''
      });

      // Clear error if any
      setError('');
    } catch (err: any) {
      console.error('Error creating assignment:', err);
      const errorMessage = err.message || err.response?.data?.detail || 'Gagal membuat assignment. Silakan coba lagi.';
      setError(errorMessage);
    } finally {
      setSubmittingAssignment(false);
    }
  };

  // Handle comment mention
  const handleCommentMention = (item: any, type: 'user' | 'group') => {
    // Check if already mentioned (prevent duplicate)
    const isAlreadyMentioned = commentMentions.some(m =>
      (type === 'user' && m.user_id === item.id) ||
      (type === 'group' && m.group_id === item.id)
    );

    if (!isAlreadyMentioned) {
      const mention = {
        user_id: type === 'user' ? item.id : undefined,
        group_id: type === 'group' ? item.id : undefined,
        mention_type: type
      };
      setCommentMentions([...commentMentions, mention]);
    }

    // Replace @searchText with @name in comment text
    const mentionText = `@${item.name}`;
    const currentText = editingCommentId ? editCommentContent : newComment;
    const cursorPos = commentMentionPosition;
    const textBeforeCursor = currentText.substring(0, cursorPos);

    // Find last @ before cursor
    const lastAt = textBeforeCursor.lastIndexOf('@');
    if (lastAt !== -1) {
      // Find where the @mention ends (space, newline, or end of string)
      // Search from @ position + 1 to end of text
      let mentionEnd = currentText.length;
      for (let i = lastAt + 1; i < currentText.length; i++) {
        if (currentText[i] === ' ' || currentText[i] === '\n') {
          mentionEnd = i;
          break;
        }
      }

      // Replace from @ to mentionEnd with @name
      const textBeforeAt = currentText.substring(0, lastAt);
      const textAfterMention = currentText.substring(mentionEnd);
      // Add space after mention if there isn't one already
      const spaceAfter = textAfterMention.startsWith(' ') || textAfterMention.startsWith('\n') ? '' : ' ';
      const newText = textBeforeAt + mentionText + spaceAfter + textAfterMention;

      if (editingCommentId) {
        setEditCommentContent(newText);
      } else {
        setNewComment(newText);
      }
    } else {
      // Fallback: just append if @ not found
      if (editingCommentId) {
        setEditCommentContent(editCommentContent + ' ' + mentionText + ' ');
      } else {
        setNewComment(newComment + ' ' + mentionText + ' ');
      }
    }

    setCommentMentionSearch('');
    setShowCommentMentionDropdown(false);
  };


  // Helper function to render comment content with styled mentions
  const renderCommentContent = (content: string, mentions: any[] = []) => {
    if (!content) return null;

    // Split content by @mentions
    const parts: Array<{ text: string; isMention: boolean; mentionData?: any }> = [];
    let lastIndex = 0;

    // Find all @mentions in the text (support names with spaces)
    // Match @ followed by word characters and spaces until space/newline/end
    const mentionRegex = /@([^\s@]+(?:\s+[^\s@]+)*)/g;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({ text: content.substring(lastIndex, match.index), isMention: false });
      }

      // Find matching mention data
      const mentionName = match[1].trim();
      const mentionData = mentions.find((m: any) => {
        if (m.mention_type === 'user' && m.user?.name === mentionName) return true;
        if (m.mention_type === 'group' && m.group?.name === mentionName) return true;
        return false;
      });

      // Add mention part
      parts.push({
        text: `@${mentionName}`,
        isMention: !!mentionData,
        mentionData: mentionData
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({ text: content.substring(lastIndex), isMention: false });
    }

    // If no mentions found, return original content
    if (parts.length === 0 || parts.every(p => !p.isMention)) {
      return <span>{content}</span>;
    }

    return (
      <span>
        {parts.map((part, index) => {
          if (part.isMention && part.mentionData) {
            const isUser = part.mentionData.mention_type === 'user';
            return (
              <span
                key={index}
                className={`font-semibold ${isUser
                  ? 'text-blue-600 hover:text-blue-700 bg-blue-50 px-1 rounded'
                  : 'text-green-600 hover:text-green-700 bg-green-50 px-1 rounded'
                  }`}
                title={isUser ? part.mentionData.user?.email : 'Group'}
              >
                {part.text}
              </span>
            );
          }
          return <span key={index}>{part.text}</span>;
        })}
      </span>
    );
  };

  // Get status colors
  const getStatusColors = (status: any) => {
    return {
      badge: status?.color_class || 'bg-gray-100 text-gray-700'
    };
  };

  if (loading) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Header />
        <div className="pt-14 flex items-center justify-center h-screen">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-4xl text-violet-600 mb-4"></i>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat detail task...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Header />
        <div className="pt-14 flex items-center justify-center h-screen">
          <div className="text-center">
            <i className="fa-solid fa-exclamation-triangle text-4xl text-red-600 mb-4"></i>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{error}</p>
            <button
              onClick={() => navigate('/board')}
              className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              Kembali ke Board
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Header />
        <div className="pt-14 flex items-center justify-center h-screen">
          <div className="text-center">
            <i className={`fa-solid fa-inbox text-4xl mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Task tidak ditemukan</p>
            <button
              onClick={() => navigate('/board')}
              className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              Kembali ke Board
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusColors = getStatusColors(task.status);

  // Handle Compare with AI - Now handled by AICompareModal component

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Header />
      <div className="pt-14">
        {/* Header Section */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4 transition-colors duration-300`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <button
                onClick={() => navigate('/board')}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                <i className="fa-solid fa-arrow-left"></i>
              </button>
              <div className="flex-1">
                {isEditingTask ? (
                  <input
                    type="text"
                    value={editTaskData?.title || ''}
                    onChange={(e) => setEditTaskData({ ...editTaskData, title: e.target.value })}
                    className={`text-2xl font-semibold border-2 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${darkMode
                      ? 'bg-gray-900 border-violet-600 text-white placeholder-gray-500'
                      : 'text-gray-900 border-violet-300'
                      }`}
                    placeholder="Masukkan title task..."
                    autoFocus
                  />
                ) : (
                  <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.title}</h1>
                )}
                {!isEditingTask && (
                  <div className="flex items-center space-x-4 mt-2">
                    <span className={`px-3 py-1 text-sm font-medium rounded ${statusColors.badge}`}>
                      {task.status?.name || 'N/A'}
                    </span>
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Dibuat {formatRelativeTime(task.created_at)} oleh {task.creator?.name || 'Unknown'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isEditingTask ? (
                <>
                  <button
                    onClick={handleCancelEditTask}
                    className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleSaveTask}
                    disabled={savingTask}
                    className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50"
                  >
                    {savingTask ? 'Menyimpan...' : 'Simpan'}
                  </button>
                </>
              ) : (
                <>
                  {task.status?.name === 'RELEASED' ? (
                    <>
                      {/* Download Document Button for Released Task */}
                      {documentVersions && documentVersions.length > 0 && (
                        <button
                          onClick={() => {
                            // Assuming the first one is the latest because of order, otherwise find is_current
                            const latest = documentVersions.find(v => v.is_current) || documentVersions[0];
                            if (latest) {
                              handleDocumentAction(latest.file_path, latest.file_name, latest.file_name.toLowerCase().endsWith('.pdf'));
                            }
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700 border-transparent`}
                        >
                          <i className="fa-solid fa-download mr-2"></i>Download Document
                        </button>
                      )}

                      {/* History Button */}
                      <button
                        onClick={() => {
                          setHistoryOpen(!historyOpen);
                          if (!historyOpen) setShowReviewComments(false);
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${historyOpen
                          ? 'bg-violet-600 text-white hover:bg-violet-700'
                          : darkMode
                            ? 'border border-gray-700 text-gray-300 hover:bg-gray-700'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <i className={`fa-solid fa-clock-rotate-left mr-2`}></i>
                        History
                      </button>
                    </>
                  ) : (
                    <>
                      {task.status?.name === 'APPROVED' ? (
                        user?.scopes?.includes('task.release') && documentVersions && documentVersions.length > 0 && (() => {
                          const latest = documentVersions.find((v: any) => v.is_current) || documentVersions[0];
                          // Create filename: task title + [APPROVED] + extension
                          const extension = latest?.file_name?.split('.').pop() || 'docx';
                          const downloadFileName = `${task.title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}_[APPROVED].${extension}`;

                          return (
                            <>
                              <button
                                onClick={() => {
                                  if (latest) {
                                    handleDocumentAction(latest.file_path, downloadFileName, extension.toLowerCase() === 'pdf');
                                  }
                                }}
                                disabled={!latest}
                                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="fa-solid fa-download mr-2"></i>
                                Download Document
                              </button>

                              {/* Release Button */}
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Apakah Anda yakin ingin merilis dokumen ini? Status akan berubah menjadi RELEASED.')) {
                                    return;
                                  }
                                  setSavingTask(true);
                                  try {
                                    if (releasedStatusId) {
                                      const updatedTask = await apiService.updateTask(Number(id), { status_id: releasedStatusId });
                                      setTask(updatedTask);
                                    } else {
                                      setError('Status RELEASED tidak ditemukan');
                                    }
                                  } catch (err: any) {
                                    console.error('Error releasing task:', err);
                                    setError(err.message || 'Gagal merilis dokumen');
                                  } finally {
                                    setSavingTask(false);
                                  }
                                }}
                                disabled={savingTask}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {savingTask ? (
                                  <>
                                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-rocket mr-2"></i>
                                    Release
                                  </>
                                )}
                              </button>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          {task.status?.name === 'DRAFTING' && (
                            <button
                              onClick={() => setShowCompareModal(true)}
                              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                            >
                              <i className="fa-solid fa-robot mr-2"></i>Compare with AI
                            </button>
                          )}
                          {task.status?.name !== 'Review' && task.status?.name !== 'REVIEW' && task.status_id !== 3 && (
                            <button
                              onClick={() => navigate(`/agreement/${id}/draft-v2`)}
                              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                            >
                              <i className="fa-solid fa-file-pen mr-2"></i>Drafting
                            </button>
                          )}
                          {task.status_id === 3 && (
                            <button
                              onClick={() => navigate(`/agreement/${id}/review`)}
                              className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700"
                            >
                              <i className="fa-solid fa-eye mr-2"></i>Review
                            </button>
                          )}

                          {!['APPROVED', 'RELEASED', 'NEW'].includes(task.status?.name) && task.status_id !== 3 && (
                            <button
                              onClick={handleSubmitToReview}
                              disabled={savingTask}
                              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {savingTask ? (
                                <>
                                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                  Mengirim...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-paper-plane mr-2"></i>
                                  Submit to Review
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )}
                      {task.status?.name !== 'NEW' && (() => {
                        const totalCount = reviewComments.length;
                        const resolvedCount = reviewComments.filter((c: any) => c.is_resolved).length;
                        const allResolved = totalCount > 0 && resolvedCount === totalCount;

                        return (
                          <>
                            <button
                              onClick={() => {
                                setShowReviewComments(!showReviewComments);
                                if (!showReviewComments) setHistoryOpen(false);
                              }}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${showReviewComments
                                ? allResolved
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                : totalCount > 0
                                  ? allResolved
                                    ? darkMode
                                      ? 'border border-green-600 text-green-400 hover:bg-green-900/30'
                                      : 'border border-green-500 text-green-600 hover:bg-green-50'
                                    : darkMode
                                      ? 'border border-yellow-600 text-yellow-400 hover:bg-yellow-900/30'
                                      : 'border border-yellow-500 text-yellow-600 hover:bg-yellow-50'
                                  : darkMode
                                    ? 'border border-gray-700 text-gray-300 hover:bg-gray-700'
                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              <i className={`fa-solid fa-comments mr-2`}></i>
                              Review {totalCount > 0 && `(${resolvedCount}/${totalCount})`}
                            </button>

                            {/* Approve Button - Only enabled when all reviews are resolved */}
                            {user?.scopes?.includes('task.approve') && (task.status_id === 3 || task.status?.name === 'REVIEW') && (
                              <button
                                onClick={handleApproveTask}
                                disabled={savingTask || !allResolved}
                                title={!allResolved ? 'Selesaikan semua review comment terlebih dahulu' : ''}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${allResolved
                                  ? 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                  }`}
                              >
                                {savingTask ? (
                                  <>
                                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-check mr-2"></i>Approve
                                  </>
                                )}
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <button
                        onClick={() => {
                          setHistoryOpen(!historyOpen);
                          if (!historyOpen) setShowReviewComments(false);
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${historyOpen
                          ? 'bg-violet-600 text-white hover:bg-violet-700'
                          : darkMode
                            ? 'border border-gray-700 text-gray-300 hover:bg-gray-700'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <i className={`fa-solid fa-clock-rotate-left mr-2`}></i>
                        Activity Report
                      </button>
                      <button
                        onClick={handleEditTask}
                        className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${darkMode
                          ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <i className="fa-solid fa-edit mr-2"></i>Edit
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="">
          <div className="px-6 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Description */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Deskripsi</h2>
                {isEditingTask ? (
                  <textarea
                    value={editTaskData?.description || ''}
                    onChange={(e) => setEditTaskData({ ...editTaskData, description: e.target.value })}
                    rows={4}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                      : 'border-gray-300'
                      }`}
                    placeholder="Masukkan deskripsi task..."
                  />
                ) : (
                  <p className={`whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{task.description || 'Tidak ada deskripsi'}</p>
                )}
              </div>



              {/* Quick Info - Moved from sidebar */}
              {/* Combined Information Section */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <div className="flex items-center mb-6">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${darkMode ? 'bg-violet-900/30' : 'bg-violet-100'
                    }`}>
                    <i className={`fa-solid fa-info-circle ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                  </div>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Informasi</h2>
                </div>

                {/* Stats Cards Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Pembuat */}
                  <div className={`p-4 rounded-xl border transition-all duration-300 ${darkMode
                    ? 'bg-gradient-to-br from-violet-900/20 to-purple-900/10 border-violet-700/50 hover:border-violet-600'
                    : 'bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200 hover:border-violet-300 hover:shadow-md'
                    }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-violet-900/50' : 'bg-violet-100'
                        }`}>
                        <i className={`fa-solid fa-user-plus ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                      </div>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-violet-300' : 'text-violet-600'
                        }`}>Pembuat</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {task.creator?.avatar_url ? (
                        <img src={task.creator.avatar_url} className="w-10 h-10 rounded-full ring-2 ring-violet-200 dark:ring-violet-800" alt={task.creator.name} />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-2 ${darkMode ? 'bg-gray-700 ring-violet-800' : 'bg-gray-300 ring-violet-200'
                          }`}>
                          <span className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {task.creator?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.creator?.name || 'Unknown'}
                        </p>
                        <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {task.creator?.email || ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Komentar */}
                  <div className={`p-4 rounded-xl border transition-all duration-300 ${darkMode
                    ? 'bg-gradient-to-br from-blue-900/20 to-cyan-900/10 border-blue-700/50 hover:border-blue-600'
                    : 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 hover:border-blue-300 hover:shadow-md'
                    }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                        }`}>
                        <i className={`fa-solid fa-comments ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
                      </div>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-blue-300' : 'text-blue-600'
                        }`}>Komentar</span>
                    </div>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{comments.length}</p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total komentar</p>
                  </div>

                  {/* Log Entries */}
                  <div className={`p-4 rounded-xl border transition-all duration-300 ${darkMode
                    ? 'bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-amber-700/50 hover:border-amber-600'
                    : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:border-amber-300 hover:shadow-md'
                    }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'
                        }`}>
                        <i className={`fa-solid fa-history ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}></i>
                      </div>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-amber-300' : 'text-amber-600'
                        }`}>Log Entries</span>
                    </div>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{logs.length}</p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total aktivitas</p>
                  </div>
                </div>

                {/* Task Details Grid */}
                <div className={`pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-semibold mb-4 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Detail Task</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Kategori</label>
                      <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {task.category?.name || 'N/A'}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tipe Agreement</label>
                      <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {task.agreement_type?.name || 'N/A'}
                      </p>
                    </div>
                    {task.company && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                        <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Perusahaan</label>
                        <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.company.name}
                        </p>
                      </div>
                    )}
                    {task.template && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                        <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Template</label>
                        <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.template.title}
                        </p>
                      </div>
                    )}
                    {task.template_type && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                        <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Template Type</label>
                        <p className="mt-1">
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${task.template_type === 'Internal'
                            ? darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                            : darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                            }`}>
                            {task.template_type}
                          </span>
                        </p>
                      </div>
                    )}
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dibuat</label>
                      <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatDate(task.created_at)}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Diupdate</label>
                      <p className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatDate(task.updated_at)}
                      </p>
                    </div>
                    
                    {/* New Fields */}
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nilai Perjanjian</label>
                      {isEditingTask ? (
                        <RupiahInput
                          size="sm"
                          className="mt-1"
                          darkMode={darkMode}
                          value={editTaskData?.agreement_amount !== undefined ? editTaskData.agreement_amount : task.agreement_amount}
                          onChange={(val) => setEditTaskData({ ...editTaskData, agreement_amount: val })}
                          disabled={!isBusinessUser}
                        />
                      ) : (
                        <p className={`mt-1 text-sm font-medium whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.agreement_amount !== null && task.agreement_amount !== undefined
                            ? formatRupiah(task.agreement_amount)
                            : (task.agreement_value || '-')}
                        </p>
                      )}
                    </div>

                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Jangka Waktu</label>
                      {isEditingTask ? (
                        <input
                          type="text"
                          value={editTaskData?.period || ''}
                          onChange={(e) => setEditTaskData({ ...editTaskData, period: e.target.value })}
                          className={`mt-1 w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 text-gray-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
                          disabled={!isBusinessUser}
                        />
                      ) : (
                        <p className={`mt-1 text-sm font-medium whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.period || '-'}
                        </p>
                      )}
                    </div>

                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cara Pembayaran</label>
                      {isEditingTask ? (
                        <textarea
                          value={editTaskData?.payment_method || ''}
                          onChange={(e) => setEditTaskData({ ...editTaskData, payment_method: e.target.value })}
                          rows={2}
                          className={`mt-1 w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 text-gray-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
                          disabled={!isBusinessUser}
                        />
                      ) : (
                        <p className={`mt-1 text-sm font-medium whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.payment_method || '-'}
                        </p>
                      )}
                    </div>

                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Jaminan</label>
                      {isEditingTask ? (
                        <textarea
                          value={editTaskData?.guarantee || ''}
                          onChange={(e) => setEditTaskData({ ...editTaskData, guarantee: e.target.value })}
                          rows={2}
                          className={`mt-1 w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 text-gray-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
                          disabled={!isBusinessUser}
                        />
                      ) : (
                        <p className={`mt-1 text-sm font-medium whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.guarantee || '-'}
                        </p>
                      )}
                    </div>

                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Denda / Ganti Rugi</label>
                      {isEditingTask ? (
                        <textarea
                          value={editTaskData?.penalty_compensation || ''}
                          onChange={(e) => setEditTaskData({ ...editTaskData, penalty_compensation: e.target.value })}
                          rows={2}
                          className={`mt-1 w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 text-gray-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
                          disabled={!isLegalUser}
                        />
                      ) : (
                        <p className={`mt-1 text-sm font-medium whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {task.penalty_compensation || '-'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mentions Section */}
                {(() => {
                  // Collect all mentions from comments
                  const allMentions: any[] = [];
                  const mentionMap = new Map<string, any>();

                  comments.forEach((comment: any) => {
                    if (comment.mentions && Array.isArray(comment.mentions)) {
                      comment.mentions.forEach((mention: any) => {
                        const key = mention.mention_type === 'user'
                          ? `user_${mention.user_id || mention.user?.id}`
                          : `group_${mention.group_id || mention.group?.id}`;

                        if (!mentionMap.has(key)) {
                          mentionMap.set(key, mention);
                          allMentions.push(mention);
                        }
                      });
                    }
                  });

                  if (task.mentions && Array.isArray(task.mentions)) {
                    task.mentions.forEach((mention: any) => {
                      const key = mention.mention_type === 'user'
                        ? `user_${mention.user_id || mention.user?.id}`
                        : `group_${mention.group_id || mention.group?.id}`;

                      if (!mentionMap.has(key)) {
                        mentionMap.set(key, mention);
                        allMentions.push(mention);
                      }
                    });
                  }

                  return allMentions.length > 0 ? (
                    <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex items-center mb-4">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-2 ${darkMode ? 'bg-violet-900/30' : 'bg-violet-100'
                          }`}>
                          <i className={`fa-solid fa-at text-sm ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Disebutkan ({allMentions.length})
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {allMentions.map((mention: any, index: number) => (
                          <div
                            key={mention.id || `mention-${index}`}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${darkMode
                              ? mention.mention_type === 'user'
                                ? 'bg-blue-900/30 border-blue-700 hover:bg-blue-900/40'
                                : 'bg-green-900/30 border-green-700 hover:bg-green-900/40'
                              : mention.mention_type === 'user'
                                ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                                : 'bg-green-50 border-green-200 hover:bg-green-100'
                              }`}
                          >
                            {mention.mention_type === 'user' && mention.user ? (
                              <>
                                {mention.user.avatar_url ? (
                                  <img src={mention.user.avatar_url} className="w-6 h-6 rounded-full ring-1 ring-blue-200 dark:ring-blue-800" alt={mention.user.name} />
                                ) : (
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ring-1 ${darkMode ? 'bg-gray-600 ring-blue-800' : 'bg-gray-300 ring-blue-200'
                                    }`}>
                                    <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {mention.user.name?.charAt(0) || 'U'}
                                    </span>
                                  </div>
                                )}
                                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {mention.user.name}
                                </span>
                                <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                  User
                                </span>
                              </>
                            ) : mention.mention_type === 'group' && mention.group ? (
                              <>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ring-1 ${darkMode ? 'bg-gray-600 ring-green-800' : 'bg-gray-300 ring-green-200'
                                  }`}>
                                  <i className={`fa-solid fa-users text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}></i>
                                </div>
                                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {mention.group.name}
                                </span>
                                <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                  }`}>
                                  Group
                                </span>
                              </>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Document History Section */}
              <div className={`mt-8 p-6 rounded-xl border shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                      <i className="fa-solid fa-clock-rotate-left text-xl"></i>
                    </div>
                    <div>
                      <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Document History</h2>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Riwayat versi dokumen perjanjian</p>
                    </div>
                  </div>

                  {/* Upload New Version Button - Only for task.editor */}
                  {user?.scopes?.includes('task.editor') && (
                    <div>
                      <button
                        onClick={() => {
                          setUploadVersionFile(null);
                          setUploadNote('');
                          setShowUploadModal(true);
                        }}
                        className={`cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${darkMode
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          }`}
                      >
                        <i className="fa-solid fa-upload mr-2"></i>
                        Upload New Version
                      </button>
                    </div>
                  )}
                </div>

                {documentVersions.length > 0 ? (
                  <div className="space-y-4">
                    {documentVersions.slice(0, showAllVersions ? documentVersions.length : 3).map((version) => (
                      <div
                        key={version.id}
                        className={`group relative p-4 rounded-xl border transition-all duration-200 ${version.is_current
                          ? darkMode ? 'bg-indigo-900/20 border-indigo-500/50' : 'bg-indigo-50 border-indigo-200'
                          : darkMode ? 'bg-gray-800/50 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm hover:shadow'
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className={`mt-1 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${version.is_current
                              ? 'bg-indigo-600 text-white'
                              : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                              v{version.version_number}
                            </div>

                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {version.file_name}
                                </h3>
                                {version.is_current && (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 uppercase tracking-wide">
                                    Current
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center space-x-4 mt-2 text-xs">
                                <div className="flex items-center space-x-1.5">
                                  <i className={`fa-regular fa-user ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                                    {version.created_by?.name || 'Unknown'}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1.5">
                                  <i className={`fa-regular fa-clock ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                                    {formatDate(version.created_at)}
                                  </span>
                                </div>
                              </div>
                              {version.note && (
                                <div className={`mt-2 text-xs p-2 rounded ${darkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-100'}`}>
                                  <i className="fa-regular fa-comment-dots mr-1.5 opacity-70"></i>
                                  {version.note}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/agreement/${id}/review?version_id=${version.id}`)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${darkMode
                                ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                              <i className="fa-solid fa-eye mr-1.5"></i>
                              View
                            </button>
                            <button
                              onClick={() => handleDocumentAction(version.file_path, version.file_name, version.file_name.toLowerCase().endsWith('.pdf'))}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${darkMode
                                ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                              <i className="fa-solid fa-download mr-1.5"></i>
                              Download
                            </button>

                          </div>
                        </div>
                      </div>
                    ))}

                    {documentVersions.length > 3 && (
                      <div className="flex justify-center mt-4 pt-2">
                        <button
                          onClick={() => setShowAllVersions(!showAllVersions)}
                          className={`flex items-center px-4 py-2 text-xs font-medium rounded-full transition-colors border ${darkMode
                            ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          {showAllVersions ? (
                            <>
                              <i className="fa-solid fa-chevron-up mr-2"></i>
                              Show Less
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-chevron-down mr-2"></i>
                              View Older Version ({documentVersions.length - 3} more)
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <i className="fa-solid fa-clock-rotate-left text-3xl mb-2"></i>
                    <p className="text-sm">Belum ada riwayat versi dokumen</p>
                  </div>
                )}
              </div>

              {/* Variables - Collapsible */}
              {task.variables && task.variables.length > 0 && (
                <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Variabel ({task.variables.length})</h2>
                    <button
                      onClick={() => setVariablesExpanded(!variablesExpanded)}
                      className="text-sm text-violet-600 hover:text-violet-700"
                    >
                      {variablesExpanded ? (
                        <>
                          <i className="fa-solid fa-chevron-up mr-1"></i>Sembunyikan
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-chevron-down mr-1"></i>Tampilkan
                        </>
                      )}
                    </button>
                  </div>
                  {variablesExpanded && (
                    <div className="space-y-4">
                      {task.variables.map((variable: any, index: number) => (
                        <div key={index} className={`border-b pb-4 last:border-0 last:pb-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{variable.name}</label>
                          <p className={`mt-1 whitespace-pre-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>{variable.value || '-'}</p>
                          {variable.group && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>PIC: {variable.group.name || variable.group_id}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Supporting Documents */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Dokumen Pendukung
                    {task.supporting_documents && Array.isArray(task.supporting_documents) && (
                      <span className={`ml-2 font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({task.supporting_documents.length})
                      </span>
                    )}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowUploadDocModal(true)}
                    className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    <i className="fa-solid fa-plus mr-2"></i>
                    Tambah Dokumen
                  </button>
                </div>
                <>
                  {task.supporting_documents && Array.isArray(task.supporting_documents) && task.supporting_documents.length > 0 ? (
                    <div className="space-y-3">
                      {task.supporting_documents.map((doc: any, index: number) => {
                        const fileName = doc.file_name || doc.name || `Document ${index + 1}`;
                        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
                        const isPdf = fileExtension === 'pdf';

                        return (
                          <div
                            key={index}
                            className={`p-4 border rounded-lg transition-colors ${darkMode
                              ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start space-x-3 flex-1 min-w-0">
                                <i className={`fa-solid ${isPdf ? 'fa-file-pdf text-red-500' : darkMode ? 'fa-file text-gray-500' : 'fa-file text-gray-400'} text-lg mt-1`}></i>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {fileName}
                                  </p>
                                  {doc.size && (
                                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  )}
                                </div>
                              </div>
                              {doc.file_path && (
                                <div className="flex items-center space-x-2 ml-4">
                                  {isPdf && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDocumentAction(doc.file_path, fileName, true);
                                      }}
                                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 ${darkMode
                                        ? 'bg-violet-900/50 text-violet-300 hover:bg-violet-900/70'
                                        : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                                        }`}
                                    >
                                      <i className="fa-solid fa-eye mr-1"></i>
                                      View
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDocumentAction(doc.file_path, fileName, false);
                                    }}
                                    className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 transition-colors flex-shrink-0"
                                  >
                                    <i className="fa-solid fa-download mr-1"></i>
                                    Download
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Metadata */}
                            <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                              <div className="grid grid-cols-1 gap-2 text-xs">
                                {doc.uploaded_by && (
                                  <div className="flex items-center space-x-2">
                                    <i className={`fa-solid fa-user ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                                      Uploaded by: <span className="font-medium">{doc.uploaded_by.name || 'Unknown'}</span>
                                    </span>
                                    {doc.uploaded_at && (
                                      <span className={`ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        ({formatDate(doc.uploaded_at)})
                                      </span>
                                    )}
                                  </div>
                                )}
                                {doc.type && (
                                  <div className="flex items-center space-x-2">
                                    <i className={`fa-solid ${doc.type === 'update' ? 'fa-arrow-up' : 'fa-plus'} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                                      Type: <span className={`font-medium px-2 py-0.5 rounded ${doc.type === 'update'
                                        ? darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                                        : darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                        }`}>
                                        {doc.type === 'update' ? 'Update' : 'New'}
                                      </span>
                                    </span>
                                    {doc.type === 'update' && doc.updated_from && (
                                      <span className={`ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        from: {doc.updated_from}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {doc.reason && (
                                  <div className="flex items-start space-x-2">
                                    <i className={`fa-solid fa-comment mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                                    <div className="flex-1">
                                      <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Reason: </span>
                                      <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>{doc.reason}</span>
                                    </div>
                                  </div>
                                )}
                                {doc.changes && (
                                  <div className={`mt-1 p-2 rounded ${darkMode ? 'bg-gray-800/50' : 'bg-blue-50'}`}>
                                    <div className="flex items-start space-x-2">
                                      <i className={`fa-solid fa-list-ul mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
                                      <div className="flex-1">
                                        <span className={`text-xs font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Changes: </span>
                                        <p className={`text-xs mt-0.5 whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{doc.changes}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      <i className="fa-solid fa-file text-3xl mb-2"></i>
                      <p className="text-sm">Tidak ada dokumen pendukung</p>
                    </div>
                  )}
                </>
              </div>

              {/* AI Document Review Section */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border transition-colors duration-300`}>
                {/* Header */}
                <div className={`p-6 ${aiReviewExpanded ? `border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}` : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-violet-900/30' : 'bg-violet-100'}`}>
                        <i className={`fa-solid fa-shield-halved ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                      </div>
                      <div>
                        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Analisa Dokumen dengan AI</h2>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Legal Compliance Review – berbasis regulasi Indonesia</p>
                      </div>
                      {aiReviewStatus === 'COMPLETED' && aiReviewResult && (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                          <i className="fa-solid fa-check mr-1"></i>Tersedia
                        </span>
                      )}
                      {(aiReviewStatus === 'PENDING' || aiReviewStatus === 'PROCESSING' || aiReviewPolling) && (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                          <i className="fa-solid fa-spinner fa-spin mr-1"></i>Sedang Dianalisa...
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setAiReviewExpanded(!aiReviewExpanded)}
                      className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                      <i className={`fa-solid ${aiReviewExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                    </button>
                  </div>
                </div>

                {aiReviewExpanded && (
                  <div className="p-6">
                    {/* Upload & Run Section */}
                    {(!aiReviewResult || aiReviewStatus !== 'COMPLETED') && (
                      <div className={`mb-6 p-5 rounded-xl border-2 border-dashed ${darkMode ? 'border-gray-600 bg-gray-900/30' : 'border-gray-300 bg-gray-50'}`}>
                        <div className="flex flex-col items-center text-center mb-4">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${darkMode ? 'bg-violet-900/30' : 'bg-violet-100'}`}>
                            <i className={`fa-solid fa-file-pdf text-2xl ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                          </div>
                          <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Upload Dokumen Kontrak (PDF)</h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF kontrak untuk dianalisa kepatuhan hukumnya terhadap regulasi Indonesia</p>
                        </div>

                        <div className="space-y-3">
                          <DragDropFileUpload 
                            id="ai-review-file-input"
                            files={aiReviewFiles}
                            setFiles={setAiReviewFiles}
                            darkMode={darkMode}
                          />
                          <div className="flex flex-col space-y-1 mt-2">
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pilih Bahasa Output (Language)</label>
                            <select
                              value={aiReviewLanguage}
                              onChange={(e) => setAiReviewLanguage(e.target.value as 'indonesia' | 'inggris')}
                              className={`w-full text-sm px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                            >
                              <option value="indonesia">Bahasa Indonesia</option>
                              <option value="inggris">Bahasa Inggris (English)</option>
                            </select>
                          </div>

                          <button
                            id="ai-review-analyze-btn"
                            onClick={handleRunAiReview}
                            disabled={aiReviewFiles.length === 0 || aiReviewLoading || aiReviewPolling}
                            className={`w-full py-3 text-sm font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 ${
                              aiReviewFiles.length > 0 && !aiReviewLoading && !aiReviewPolling
                                ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg shadow-violet-500/25'
                                : darkMode
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {aiReviewLoading || aiReviewPolling ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin"></i>
                                <span>Menganalisa Dokumen...</span>
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-robot"></i>
                                <span>Analisa dengan AI</span>
                              </>
                            )}
                          </button>

                          {(aiReviewLoading || aiReviewPolling) && (
                            <div className={`p-3 rounded-lg text-center text-xs ${darkMode ? 'bg-violet-900/20 text-violet-300' : 'bg-violet-50 text-violet-700'}`}>
                              <i className="fa-solid fa-circle-notch fa-spin mr-2"></i>
                              AI sedang menganalisa dokumen Anda. Proses ini membutuhkan 1-3 menit. Mohon tunggu...
                            </div>
                          )}

                          {aiReviewStatus === 'FAILED' && (
                            <div className={`p-3 rounded-lg text-center text-xs ${darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                              <i className="fa-solid fa-exclamation-circle mr-2"></i>
                              Analisa gagal. Silakan coba upload ulang dan jalankan kembali.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Results Section */}
                    {aiReviewResult && aiReviewStatus === 'COMPLETED' && (() => {
                      const result = aiReviewResult;
                      const summary = result.compliance_summary || {};
                      const dashboard = result.dashboard || {};
                      const score = summary.overall_score || dashboard.gauge_score || 0;
                      const risk = summary.overall_risk || '';
                      const stats = summary.statistics || {};
                      const categories = result.compliance_categories || [];
                      const findings = result.detailed_findings || [];
                      const execSummary = result.executive_summary || {};
                      const priorityRecs = result.priority_recommendations || {};
                      const reviewInfo = result.review_information || {};

                      const scoreColor = score >= 90 ? '#22c55e' : score >= 75 ? '#eab308' : score >= 50 ? '#f97316' : '#ef4444';

                      return (
                        <div className="space-y-6">
                          {/* Re-run option */}
                          <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <i className="fa-solid fa-clock mr-1"></i>
                              Dianalisa pada: {reviewInfo.review_date || '-'}
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="file"
                                id="ai-review-rerun-input"
                                accept=".pdf"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files && e.target.files.length > 0) {
                                    setAiReviewFiles(Array.from(e.target.files));
                                    setAiReviewResult(null);
                                    setAiReviewStatus('');
                                  }
                                }}
                                className="hidden"
                              />
                              <label
                                htmlFor="ai-review-rerun-input"
                                className={`cursor-pointer px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                <i className="fa-solid fa-rotate mr-1"></i>Jalankan Ulang
                              </label>
                            </div>
                          </div>

                          {/* Score Dashboard */}
                          <div className={`p-6 rounded-xl border ${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'}`}>
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                              {/* Gauge */}
                              <div className="flex flex-col items-center">
                                <div className="relative" style={{ width: 140, height: 140 }}>
                                  <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                                    <circle cx="70" cy="70" r="58" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="14" />
                                    <circle
                                      cx="70" cy="70" r="58" fill="none"
                                      stroke={scoreColor}
                                      strokeWidth="14"
                                      strokeLinecap="round"
                                      strokeDasharray={`${(score / 100) * 364.4} 364.4`}
                                    />
                                  </svg>
                                  <div className="absolute inset-0 flex flex-col items-center justify-center rotate-0">
                                    <span className="text-3xl font-black" style={{ color: scoreColor }}>{score}</span>
                                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>/100</span>
                                  </div>
                                </div>
                                <p className={`text-sm font-semibold mt-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Overall Score</p>
                                <span className={`mt-1 px-3 py-1 text-xs font-bold rounded-full`} style={{ backgroundColor: scoreColor + '20', color: scoreColor }}>
                                  {risk || (score >= 90 ? 'Low Risk' : score >= 75 ? 'Moderate Risk' : score >= 50 ? 'High Risk' : 'Critical Risk')}
                                </span>
                              </div>

                              {/* Stats */}
                              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 w-full">
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-green-500">{stats.compliant || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Compliant</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-yellow-500">{stats.needs_improvement || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Needs Improvement</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-red-500">{stats.non_compliant || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Non-Compliant</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className={`text-2xl font-black ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{stats.missing || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Missing</p>
                                </div>
                              </div>
                            </div>

                            {summary.summary && (
                              <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-800/60' : 'bg-blue-50 border border-blue-100'}`}>
                                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-blue-800'}`}>{summary.summary}</p>
                              </div>
                            )}
                          </div>

                          {/* Tabs */}
                          <div className={`flex items-center space-x-1 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            {(['summary', 'categories', 'findings', 'recommendations'] as const).map(tab => (
                              <button
                                key={tab}
                                onClick={() => setAiReviewActiveTab(tab)}
                                className={`px-4 py-2.5 text-xs font-semibold capitalize transition-colors border-b-2 -mb-px ${
                                  aiReviewActiveTab === tab
                                    ? `border-violet-600 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`
                                    : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                                }`}
                              >
                                {tab === 'summary' ? 'Executive Summary' : tab === 'categories' ? 'Compliance by Category' : tab === 'findings' ? `Findings (${findings.length})` : 'Recommendations'}
                              </button>
                            ))}
                          </div>

                          {/* Tab Content */}
                          {aiReviewActiveTab === 'summary' && (
                            <div className="space-y-4">
                              {execSummary.overall_assessment && (
                                <div className={`p-5 rounded-xl border-l-4 border-violet-500 ${darkMode ? 'bg-violet-900/10' : 'bg-violet-50'}`}>
                                  <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-violet-300' : 'text-violet-800'}`}>Overall Assessment</h4>
                                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{execSummary.overall_assessment}</p>
                                </div>
                              )}
                              {execSummary.major_risks && execSummary.major_risks.length > 0 && (
                                <div className={`p-5 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
                                  <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>Major Risks</h4>
                                  <ul className="space-y-2">
                                    {execSummary.major_risks.filter((r: string) => r).map((risk: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        {risk}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {execSummary.final_recommendation && (
                                <div className={`p-5 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900/10' : 'bg-green-50'}`}>
                                  <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>Final Recommendation</h4>
                                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{execSummary.final_recommendation}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {aiReviewActiveTab === 'categories' && (
                            <div className={`overflow-hidden rounded-xl border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className={`${darkMode ? 'bg-gray-900/70' : 'bg-gray-50'}`}>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Kategori</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Score</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Risk</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Summary</th>
                                  </tr>
                                </thead>
                                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                  {categories.map((cat: any, i: number) => (
                                    <tr key={i} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                                      <td className={`px-4 py-3 font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{cat.category}</td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getComplianceStatusColor(cat.status)}`}>
                                          {cat.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center space-x-2">
                                          <div className={`w-24 h-1.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                            <div
                                              className="h-1.5 rounded-full transition-all"
                                              style={{ width: `${cat.score || 0}%`, backgroundColor: cat.score >= 75 ? '#22c55e' : cat.score >= 50 ? '#eab308' : '#ef4444' }}
                                            />
                                          </div>
                                          <span className={`text-xs font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{cat.score}</span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`text-xs font-medium`} style={{ color: getRiskColor(cat.risk) }}>{cat.risk}</span>
                                      </td>
                                      <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cat.summary}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {aiReviewActiveTab === 'findings' && (
                            <div className="space-y-4">
                              {findings.length === 0 ? (
                                <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tidak ada findings</p>
                              ) : (
                                findings.map((finding: any) => (
                                  <div key={finding.id} className={`p-5 rounded-xl border ${darkMode ? 'bg-gray-900/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getComplianceStatusColor(finding.status)}`}>
                                          {finding.status}
                                        </span>
                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded ${getSeverityColor(finding.severity)}`}>
                                          {finding.severity}
                                        </span>
                                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{finding.category}</span>
                                      </div>
                                      <span className={`text-xs font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>#{finding.id}</span>
                                    </div>
                                    <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{finding.clause_title}</h4>
                                    {finding.issue && (
                                      <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.issue}</p>
                                    )}
                                    {finding.analysis && (
                                      <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                        <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.analysis}</p>
                                      </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                      {finding.applicable_regulation && (
                                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                          <span className="font-medium">Regulasi: </span>{finding.applicable_regulation}
                                          {finding.article && <span className="ml-1 text-violet-500">({finding.article})</span>}
                                        </div>
                                      )}
                                      {finding.legal_risk && (
                                        <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                                          <span className="font-medium">Legal Risk: </span>{finding.legal_risk}
                                        </div>
                                      )}
                                    </div>
                                    {finding.recommendation && (
                                      <div className={`mt-3 p-3 rounded-lg border-l-3 border-l-2 border-violet-500 ${darkMode ? 'bg-violet-900/10' : 'bg-violet-50'}`}>
                                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                                          <i className="fa-solid fa-lightbulb mr-1"></i>Rekomendasi
                                        </p>
                                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.recommendation}</p>
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}

                          {aiReviewActiveTab === 'recommendations' && (
                            <div className="space-y-4">
                              {priorityRecs.high && priorityRecs.high.filter((r: string) => r).length > 0 && (
                                <div>
                                  <h4 className={`flex items-center text-sm font-bold mb-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mr-2 ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>!</span>
                                    HIGH PRIORITY
                                  </h4>
                                  <ul className="space-y-2">
                                    {priorityRecs.high.filter((r: string) => r).map((rec: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm p-3 rounded-lg ${darkMode ? 'bg-red-900/10 border border-red-900/30' : 'bg-red-50 border border-red-100'}`}>
                                        <i className="fa-solid fa-chevron-right text-red-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {priorityRecs.medium && priorityRecs.medium.filter((r: string) => r).length > 0 && (
                                <div>
                                  <h4 className={`flex items-center text-sm font-bold mb-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mr-2 ${darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>~</span>
                                    MEDIUM PRIORITY
                                  </h4>
                                  <ul className="space-y-2">
                                    {priorityRecs.medium.filter((r: string) => r).map((rec: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm p-3 rounded-lg ${darkMode ? 'bg-yellow-900/10 border border-yellow-900/30' : 'bg-yellow-50 border border-yellow-100'}`}>
                                        <i className="fa-solid fa-chevron-right text-yellow-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {priorityRecs.low && priorityRecs.low.filter((r: string) => r).length > 0 && (
                                <div>
                                  <h4 className={`flex items-center text-sm font-bold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mr-2 ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>i</span>
                                    LOW PRIORITY
                                  </h4>
                                  <ul className="space-y-2">
                                    {priorityRecs.low.filter((r: string) => r).map((rec: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm p-3 rounded-lg ${darkMode ? 'bg-blue-900/10 border border-blue-900/30' : 'bg-blue-50 border border-blue-100'}`}>
                                        <i className="fa-solid fa-chevron-right text-blue-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* AI Enterprise Risk Assessment Section (agent_risk) */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border transition-colors duration-300`}>
                <div className={`p-6 ${aiRiskExpanded ? `border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}` : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                        <i className={`fa-solid fa-triangle-exclamation ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}></i>
                      </div>
                      <div>
                        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Enterprise Risk Assessment dengan AI</h2>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Analisa risiko legal, finansial, operasional & kepatuhan kontrak</p>
                      </div>
                      {aiRiskStatus === 'COMPLETED' && aiRiskResult && (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                          <i className="fa-solid fa-check mr-1"></i>Tersedia
                        </span>
                      )}
                      {(aiRiskStatus === 'PENDING' || aiRiskStatus === 'PROCESSING' || aiRiskPolling) && (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                          <i className="fa-solid fa-spinner fa-spin mr-1"></i>Sedang Menganalisa Risiko...
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setAiRiskExpanded(!aiRiskExpanded)}
                      className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                      <i className={`fa-solid ${aiRiskExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                    </button>
                  </div>
                </div>

                {aiRiskExpanded && (
                  <div className="p-6">
                    {(!aiRiskResult || aiRiskStatus !== 'COMPLETED') && (
                      <div className={`mb-6 p-5 rounded-xl border-2 border-dashed ${darkMode ? 'border-gray-600 bg-gray-900/30' : 'border-gray-300 bg-gray-50'}`}>
                        <div className="flex flex-col items-center text-center mb-4">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                            <i className={`fa-solid fa-file-pdf text-2xl ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}></i>
                          </div>
                          <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Upload Dokumen Kontrak (PDF)</h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF kontrak untuk identifikasi & kuantifikasi risiko enterprise</p>
                        </div>

                        <div className="space-y-3">
                          <DragDropFileUpload 
                            id="ai-risk-file-input"
                            files={aiRiskFiles}
                            setFiles={setAiRiskFiles}
                            darkMode={darkMode}
                          />
                          <div className="flex flex-col space-y-1 mt-2">
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pilih Bahasa Output (Language)</label>
                            <select
                              value={aiRiskLanguage}
                              onChange={(e) => setAiRiskLanguage(e.target.value as 'indonesia' | 'inggris')}
                              className={`w-full text-sm px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                            >
                              <option value="indonesia">Bahasa Indonesia</option>
                              <option value="inggris">Bahasa Inggris (English)</option>
                            </select>
                          </div>

                          <button
                            id="ai-risk-analyze-btn"
                            onClick={handleRunAiRisk}
                            disabled={aiRiskFiles.length === 0 || aiRiskLoading || aiRiskPolling}
                            className={`w-full py-3 text-sm font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 ${
                              aiRiskFiles.length > 0 && !aiRiskLoading && !aiRiskPolling
                                ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25'
                                : darkMode
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {aiRiskLoading || aiRiskPolling ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin"></i>
                                <span>Menganalisa Risiko Kontrak...</span>
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-robot"></i>
                                <span>Analisa Risiko dengan AI</span>
                              </>
                            )}
                          </button>

                          {(aiRiskLoading || aiRiskPolling) && (
                            <div className={`p-3 rounded-lg text-center text-xs ${darkMode ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                              <i className="fa-solid fa-circle-notch fa-spin mr-2"></i>
                              AI sedang memetakan risiko enterprise dari dokumen Anda. Proses ini membutuhkan 1-3 menit...
                            </div>
                          )}

                          {aiRiskStatus === 'FAILED' && (
                            <div className={`p-3 rounded-lg text-center text-xs ${darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                              <i className="fa-solid fa-exclamation-circle mr-2"></i>
                              Analisa risiko gagal. Silakan coba upload ulang dan jalankan kembali.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {aiRiskResult && aiRiskStatus === 'COMPLETED' && (() => {
                      const result = aiRiskResult;
                      const summary = result.risk_summary || {};
                      const score = summary.overall_risk_score || 0;
                      const rating = summary.overall_risk_rating || '';
                      const dist = summary.risk_distribution || {};
                      const categories = result.risk_by_category || [];
                      const register = result.risk_register || [];
                      const execSummary = result.executive_summary || {};
                      const stratRecs = result.strategic_recommendations || {};
                      const reviewInfo = result.review_information || {};

                      const scoreColor = score <= 4 ? '#22c55e' : score <= 9 ? '#eab308' : score <= 16 ? '#f97316' : '#ef4444';

                      return (
                        <div className="space-y-6">
                          <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <i className="fa-solid fa-clock mr-1"></i>
                              Dianalisa pada: {reviewInfo.review_date || '-'}
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="file"
                                id="ai-risk-rerun-input"
                                accept=".pdf"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files && e.target.files.length > 0) {
                                    setAiRiskFiles(Array.from(e.target.files));
                                    setAiRiskResult(null);
                                    setAiRiskStatus('');
                                  }
                                }}
                                className="hidden"
                              />
                              <label
                                htmlFor="ai-risk-rerun-input"
                                className={`cursor-pointer px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                <i className="fa-solid fa-rotate mr-1"></i>Jalankan Ulang
                              </label>
                            </div>
                          </div>

                          {/* Risk Dashboard */}
                          <div className={`p-6 rounded-xl border ${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'}`}>
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                              <div className="flex flex-col items-center">
                                <div className="relative" style={{ width: 140, height: 140 }}>
                                  <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                                    <circle cx="70" cy="70" r="58" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="14" />
                                    <circle
                                      cx="70" cy="70" r="58" fill="none"
                                      stroke={scoreColor}
                                      strokeWidth="14"
                                      strokeLinecap="round"
                                      strokeDasharray={`${(score / 25) * 364.4} 364.4`}
                                    />
                                  </svg>
                                  <div className="absolute inset-0 flex flex-col items-center justify-center rotate-0">
                                    <span className="text-3xl font-black" style={{ color: scoreColor }}>{score}</span>
                                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>/25 Score</span>
                                  </div>
                                </div>
                                <p className={`text-sm font-semibold mt-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Risk Level</p>
                                <span className="mt-1 px-3 py-1 text-xs font-bold rounded-full" style={{ backgroundColor: scoreColor + '20', color: scoreColor }}>
                                  {rating || (score <= 4 ? 'Low Risk' : score <= 9 ? 'Medium Risk' : score <= 16 ? 'High Risk' : 'Critical Risk')}
                                </span>
                              </div>

                              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 w-full">
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-red-500">{dist.critical || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Critical</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-orange-500">{dist.high || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>High</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-yellow-500">{dist.medium || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Medium</p>
                                </div>
                                <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                                  <p className="text-2xl font-black text-green-500">{dist.low || 0}</p>
                                  <p className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Low</p>
                                </div>
                              </div>
                            </div>

                            {execSummary.overall_assessment && (
                              <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-800/60' : 'bg-amber-50 border border-amber-100'}`}>
                                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-amber-900'}`}>{execSummary.overall_assessment}</p>
                              </div>
                            )}
                          </div>

                          {/* Tabs */}
                          <div className={`flex items-center space-x-1 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            {(['summary', 'register', 'categories', 'recommendations'] as const).map(tab => (
                              <button
                                key={tab}
                                onClick={() => setAiRiskActiveTab(tab)}
                                className={`px-4 py-2.5 text-xs font-semibold capitalize transition-colors border-b-2 -mb-px ${
                                  aiRiskActiveTab === tab
                                    ? `border-amber-600 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`
                                    : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                                }`}
                              >
                                {tab === 'summary' ? 'Executive Summary' : tab === 'register' ? `Risk Register (${register.length})` : tab === 'categories' ? 'By Category' : 'Strategic Actions'}
                              </button>
                            ))}
                          </div>

                          {/* Tab Content */}
                          {aiRiskActiveTab === 'summary' && (
                            <div className="space-y-4">
                              {execSummary.critical_findings && execSummary.critical_findings.length > 0 && (
                                <div className={`p-5 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
                                  <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>Critical Findings</h4>
                                  <ul className="space-y-2">
                                    {execSummary.critical_findings.filter((r: string) => r).map((finding: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        {finding}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {execSummary.recommendation && (
                                <div className={`p-5 rounded-xl border-l-4 border-amber-500 ${darkMode ? 'bg-amber-900/10' : 'bg-amber-50'}`}>
                                  <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Final Recommendation & Approval</h4>
                                  <p className={`text-sm leading-relaxed mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{execSummary.recommendation}</p>
                                  {execSummary.approval_status && (
                                    <div className="mt-2 text-xs font-bold uppercase tracking-wide px-2.5 py-1 inline-block rounded bg-amber-500/20 text-amber-500">
                                      Status: {execSummary.approval_status}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {aiRiskActiveTab === 'register' && (
                            <div className="space-y-4">
                              {register.length === 0 ? (
                                <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tidak ada risk register</p>
                              ) : (
                                register.map((risk: any) => (
                                  <div key={risk.risk_id} className={`p-5 rounded-xl border ${darkMode ? 'bg-gray-900/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getRiskLevelBadge(risk.risk_level)}`}>
                                          {risk.risk_level} (Score: {risk.risk_score})
                                        </span>
                                        <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{risk.category}</span>
                                      </div>
                                      <span className={`text-xs font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>#RISK-{risk.risk_id}</span>
                                    </div>
                                    <h4 className={`text-sm font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{risk.risk_name}</h4>
                                    <p className={`text-xs font-medium mb-3 text-amber-500`}>Klausul: {risk.affected_clause}</p>
                                    
                                    {risk.issue && (
                                      <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{risk.issue}</p>
                                    )}

                                    {risk.contract_excerpt && (
                                      <div className={`p-3 rounded-lg mb-3 font-mono text-xs ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                                        "{risk.contract_excerpt}"
                                      </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-3 text-xs">
                                      {risk.legal_impact && (
                                        <div className={darkMode ? 'text-red-400' : 'text-red-700'}>
                                          <span className="font-semibold">Legal Impact: </span>{risk.legal_impact}
                                        </div>
                                      )}
                                      {risk.financial_exposure > 0 && (
                                        <div className={darkMode ? 'text-orange-400' : 'text-orange-700'}>
                                          <span className="font-semibold">Financial Exposure: </span>Rp {risk.financial_exposure.toLocaleString('id-ID')}
                                        </div>
                                      )}
                                    </div>

                                    {risk.mitigation && (
                                      <div className={`mt-3 p-3 rounded-lg border-l-2 border-amber-500 ${darkMode ? 'bg-amber-900/10' : 'bg-amber-50'}`}>
                                        <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                                          <i className="fa-solid fa-shield-halved mr-1"></i>Mitigasi & Rekomendasi
                                        </p>
                                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{risk.mitigation}</p>
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}

                          {aiRiskActiveTab === 'categories' && (
                            <div className={`overflow-hidden rounded-xl border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className={`${darkMode ? 'bg-gray-900/70' : 'bg-gray-50'}`}>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Kategori</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Risks</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rating</th>
                                    <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Summary</th>
                                  </tr>
                                </thead>
                                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                  {categories.map((cat: any, i: number) => (
                                    <tr key={i} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                                      <td className={`px-4 py-3 font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{cat.category}</td>
                                      <td className="px-4 py-3 font-bold">{cat.total_risks}</td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getRiskLevelBadge(cat.risk_rating)}`}>
                                          {cat.risk_rating}
                                        </span>
                                      </td>
                                      <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cat.summary}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {aiRiskActiveTab === 'recommendations' && (
                            <div className="space-y-4">
                              {stratRecs.immediate && stratRecs.immediate.filter((r: string) => r).length > 0 && (
                                <div>
                                  <h4 className={`flex items-center text-sm font-bold mb-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mr-2 ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>!</span>
                                    IMMEDIATE ACTIONS (SEGERA)
                                  </h4>
                                  <ul className="space-y-2">
                                    {stratRecs.immediate.filter((r: string) => r).map((rec: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm p-3 rounded-lg ${darkMode ? 'bg-red-900/10 border border-red-900/30' : 'bg-red-50 border border-red-100'}`}>
                                        <i className="fa-solid fa-chevron-right text-red-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {stratRecs.short_term && stratRecs.short_term.filter((r: string) => r).length > 0 && (
                                <div>
                                  <h4 className={`flex items-center text-sm font-bold mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mr-2 ${darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>~</span>
                                    SHORT TERM ACTIONS (JANGKA PENDEK)
                                  </h4>
                                  <ul className="space-y-2">
                                    {stratRecs.short_term.filter((r: string) => r).map((rec: string, i: number) => (
                                      <li key={i} className={`flex items-start text-sm p-3 rounded-lg ${darkMode ? 'bg-amber-900/10 border border-amber-900/30' : 'bg-amber-50 border border-amber-100'}`}>
                                        <i className="fa-solid fa-chevron-right text-amber-500 mt-0.5 mr-2 flex-shrink-0 text-xs"></i>
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <div className="flex items-center mb-4">
                  <i className={`fa-solid fa-user-plus mr-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assign Task</h2>
                </div>

                {error && (
                  <div className={`mb-4 p-3 rounded-lg border ${darkMode
                    ? 'bg-red-900/30 border-red-800 text-red-300'
                    : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                    <div className="flex items-center">
                      <i className="fa-solid fa-exclamation-circle mr-2"></i>
                      <span className="text-sm">{error}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleAssignmentSubmit(); }} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Assign to User <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignmentForm.user_id}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, user_id: e.target.value })}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      required
                    >
                      <option value="">-- Pilih User --</option>
                      {users
                        .filter((u: any) => u.is_active !== false)
                        .map((user: any) => (
                          <option key={user.id} value={user.id}>
                            {user.name} {user.email ? `(${user.email})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Comment (Optional)
                    </label>
                    <textarea
                      value={assignmentForm.comment}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, comment: e.target.value })}
                      placeholder="Tambahkan komentar untuk assignment ini..."
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAssignmentForm({ user_id: '', comment: '' });
                        setError('');
                      }}
                      disabled={submittingAssignment}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                        ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      disabled={!assignmentForm.user_id || submittingAssignment}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center ${assignmentForm.user_id && !submittingAssignment
                        ? 'bg-violet-600 hover:bg-violet-700'
                        : 'bg-gray-400 cursor-not-allowed'
                        }`}
                    >
                      {submittingAssignment ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                          Assigning...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-user-plus mr-2"></i>
                          Assign Task
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Assignment History Section */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <div className="flex items-center mb-4">
                  <i className={`fa-solid fa-clock-rotate-left mr-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assignment History</h2>
                </div>

                {loadingAssignments ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
                    <p className="text-sm">Memuat assignment history...</p>
                  </div>
                ) : assignments.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className={`fa-solid fa-inbox text-3xl mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}></i>
                    <p className="text-sm">Belum ada assignment</p>
                  </div>
                ) : (
                  <div className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-5 my-4 space-y-8">
                    {assignments.map((assignment) => (
                      <div key={assignment.id} className="relative pl-8">
                        {/* Avatar/Icon on Timeline */}
                        <div className={`absolute -left-[1.35rem] top-0 flex items-center justify-center w-11 h-11 rounded-full border-4 border-white dark:border-gray-800 ${darkMode ? 'bg-violet-900 text-violet-200' : 'bg-violet-100 text-violet-600'
                          }`}>
                          <i className="fa-solid fa-user text-sm"></i>
                        </div>

                        {/* Card Content */}
                        <div className={`p-4 rounded-xl border transition-all duration-300 ${darkMode
                          ? 'bg-gray-800 border-gray-700 hover:border-violet-600/50 hover:shadow-lg hover:shadow-violet-900/20'
                          : 'bg-white border-gray-200 hover:border-violet-300 hover:shadow-md hover:shadow-violet-100'
                          }`}>

                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <div className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {assignment.user?.name || 'Unknown User'}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {assignment.user?.email || ''}
                              </div>
                            </div>
                            <div className={`flex flex-col items-end text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <div className="flex items-center">
                                <i className="fa-solid fa-clock mr-1"></i>
                                {formatDate(assignment.created_at)}
                              </div>
                              <span className={`mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                SLA: {Math.floor((new Date().getTime() - new Date(assignment.created_at).getTime()) / (1000 * 60 * 60 * 24))} Hari
                              </span>
                            </div>
                          </div>

                          {assignment.comment && (
                            <div className={`mt-3 p-3 rounded-lg relative ${darkMode
                              ? 'bg-gray-900/50 border border-gray-700'
                              : 'bg-violet-50 border border-violet-100'
                              }`}>
                              {/* Speech bubble triangle */}
                              <div className={`absolute top-[-6px] left-4 w-3 h-3 transform rotate-45 border-t border-l ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-violet-50 border-violet-100'
                                }`}></div>

                              <div className="flex items-start space-x-2 relative z-10">
                                <i className={`fa-solid fa-comment-dots mt-1 flex-shrink-0 ${darkMode ? 'text-violet-400' : 'text-violet-600'
                                  }`}></i>
                                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {assignment.comment}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className={`mt-3 pt-3 border-t flex items-center space-x-1 text-xs ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
                            <i className="fa-solid fa-share-from-square mr-1"></i>
                            <span>Assigned by <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{assignment.creator?.name || 'Unknown'}</span></span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Comments Section */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Komentar ({comments.length})</h2>

                {/* Add Comment Form */}
                <form onSubmit={handleSubmitComment} className="mb-6">
                  <div className="relative">
                    <textarea
                      value={newComment}
                      onChange={(e) => {
                        setNewComment(e.target.value);
                        const cursorPos = e.target.selectionStart;
                        const textBeforeCursor = e.target.value.substring(0, cursorPos);
                        const lastAt = textBeforeCursor.lastIndexOf('@');
                        if (lastAt !== -1) {
                          const textAfterAt = textBeforeCursor.substring(lastAt + 1);
                          if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                            setCommentMentionSearch(textAfterAt);
                            setCommentMentionPosition(cursorPos);
                            setShowCommentMentionDropdown(true);
                          } else {
                            setShowCommentMentionDropdown(false);
                          }
                        } else {
                          setShowCommentMentionDropdown(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '@') {
                          setShowCommentMentionDropdown(true);
                        }
                      }}
                      placeholder="Tulis komentar... (gunakan @ untuk mention)"
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'border-gray-300'
                        }`}
                    />
                    {showCommentMentionDropdown && (
                      <div className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        {users.filter(u => u.name.toLowerCase().includes(commentMentionSearch.toLowerCase())).slice(0, 5).map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleCommentMention(user, 'user')}
                            className={`w-full px-4 py-2 text-left flex items-center space-x-2 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                              }`}
                          >
                            {user.avatar_url ? (
                              <img src={user.avatar_url} className="w-8 h-8 rounded-full" alt={user.name} />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'
                                }`}>
                                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.name?.charAt(0) || 'U'}</span>
                              </div>
                            )}
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                            </div>
                          </button>
                        ))}
                        {groups.filter(g => g.name.toLowerCase().includes(commentMentionSearch.toLowerCase())).slice(0, 5).map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => handleCommentMention(group, 'group')}
                            className={`w-full px-4 py-2 text-left flex items-center space-x-2 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                              }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'
                              }`}>
                              <i className={`fa-solid fa-users text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}></i>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{group.name}</p>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Group</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {commentMentions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {commentMentions
                        .filter((mention, index, self) => {
                          // Remove duplicates: keep only first occurrence
                          return index === self.findIndex(m =>
                            (mention.mention_type === 'user' && m.mention_type === 'user' && m.user_id === mention.user_id) ||
                            (mention.mention_type === 'group' && m.mention_type === 'group' && m.group_id === mention.group_id)
                          );
                        })
                        .map((mention, index) => {
                          const user = mention.user_id ? users.find(u => u.id === mention.user_id) : null;
                          const group = mention.group_id ? groups.find(g => g.id === mention.group_id) : null;
                          return (
                            <span key={`${mention.mention_type}-${mention.user_id || mention.group_id}-${index}`} className={`px-2 py-1 text-xs rounded ${darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'
                              }`}>
                              {mention.mention_type === 'user' && user ? `@${user.name}` : mention.mention_type === 'group' && group ? `@${group.name}` : ''}
                            </span>
                          );
                        })}
                    </div>
                  )}
                  <div className="flex justify-end mt-2">
                    <button
                      type="submit"
                      disabled={submittingComment || !newComment.trim()}
                      className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingComment ? 'Mengirim...' : 'Kirim Komentar'}
                    </button>
                  </div>
                </form>

                {/* Comments List */}
                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Belum ada komentar</p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className={`border-b pb-4 last:border-0 last:pb-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        {editingCommentId === comment.id ? (
                          <div>
                            <textarea
                              value={editCommentContent}
                              onChange={(e) => setEditCommentContent(e.target.value)}
                              rows={3}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent mb-2 ${darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                                }`}
                            />
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={cancelEditing}
                                className={`px-3 py-1 text-sm border rounded-lg transition-colors ${darkMode
                                  ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                                  : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                              >
                                Batal
                              </button>
                              <button
                                onClick={() => handleUpdateComment(comment.id)}
                                disabled={!editCommentContent.trim()}
                                className="px-3 py-1 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                              >
                                Simpan
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start space-x-3">
                              {comment.user?.avatar_url ? (
                                <img src={comment.user.avatar_url} className="w-10 h-10 rounded-full" alt={comment.user.name} />
                              ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'
                                  }`}>
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{comment.user?.name?.charAt(0) || 'U'}</span>
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-2">
                                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{comment.user?.name || 'Unknown'}</span>
                                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatRelativeTime(comment.created_at)}</span>
                                  </div>
                                  {comment.user_id === user?.id && (
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => startEditingComment(comment)}
                                        className="text-xs text-violet-600 hover:text-violet-700"
                                      >
                                        <i className="fa-solid fa-edit mr-1"></i>Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="text-xs text-red-600 hover:text-red-700"
                                      >
                                        <i className="fa-solid fa-trash mr-1"></i>Hapus
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <p className={`whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {renderCommentContent(comment.content, comment.mentions || [])}
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Audit Log Section */}
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 transition-colors duration-300`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Audit Log</h2>
                <div className="space-y-4">
                  {logs.length === 0 ? (
                    <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Belum ada log</p>
                  ) : (
                    <>
                      {logs.map((log) => (
                        <div key={log.id} className={`flex items-start space-x-3 border-b pb-4 last:border-0 last:pb-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-violet-900/50' : 'bg-violet-100'
                            }`}>
                            <i className={`fa-solid fa-history text-xs ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{log.action}</span>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatRelativeTime(log.created_at)}</span>
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{log.description}</p>
                            {log.user_id && (
                              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Oleh: User ID {log.user_id}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {logsHasMore && (
                        <div className="flex justify-center pt-4">
                          <button
                            onClick={handleLoadMoreLogs}
                            disabled={loadingMoreLogs}
                            className="px-4 py-2 text-sm text-violet-600 hover:text-violet-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingMoreLogs ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                Memuat...
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-chevron-down mr-2"></i>
                                Muat Lebih Banyak
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Document Modal */}
      {showUploadDocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Tambah Dokumen Pendukung
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadDocModal(false);
                    setUploadDocForm({
                      file: null,
                      type: 'new',
                      reason: '',
                      changes: '',
                      updated_from: ''
                    });
                    setError('');
                  }}
                  className={`text-gray-400 hover:text-gray-600 ${darkMode ? 'hover:text-gray-300' : ''}`}
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    File <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    onChange={handleFileSelectForUpload}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm ${darkMode
                      ? 'bg-gray-900 border-gray-700 text-white'
                      : 'border-gray-300'
                      }`}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  />
                  {uploadDocForm.file && (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Selected: {uploadDocForm.file.name} ({(uploadDocForm.file.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>

                {/* Type */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Type Dokumen <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={uploadDocForm.type}
                    onChange={(e) => setUploadDocForm(prev => ({ ...prev, type: e.target.value as 'new' | 'update', changes: '', updated_from: '' }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm ${darkMode
                      ? 'bg-gray-900 border-gray-700 text-white'
                      : 'bg-gray-50 border-gray-300'
                      }`}
                  >
                    <option value="new">Baru</option>
                    <option value="update">Update dari dokumen sebelumnya</option>
                  </select>
                </div>

                {/* Updated From (only if type is update) */}
                {uploadDocForm.type === 'update' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Update dari Dokumen <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={uploadDocForm.updated_from}
                      onChange={(e) => setUploadDocForm(prev => ({ ...prev, updated_from: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white'
                        : 'bg-gray-50 border-gray-300'
                        }`}
                    >
                      <option value="">-- Pilih Dokumen --</option>
                      {task.supporting_documents && Array.isArray(task.supporting_documents) && task.supporting_documents.map((doc: any, idx: number) => {
                        const docName = doc.file_name || doc.name || `Document ${idx + 1}`;
                        return (
                          <option key={idx} value={docName}>{docName}</option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Alasan <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={uploadDocForm.reason}
                    onChange={(e) => setUploadDocForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Masukkan alasan mengupload file ini"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm ${darkMode
                      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                      : 'border-gray-300'
                      }`}
                    required
                  />
                </div>

                {/* Changes (only if type is update) */}
                {uploadDocForm.type === 'update' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Perubahan <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={uploadDocForm.changes}
                      onChange={(e) => setUploadDocForm(prev => ({ ...prev, changes: e.target.value }))}
                      placeholder="Masukkan informasi perubahan yang dilakukan dari dokumen sebelumnya"
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'border-gray-300'
                        }`}
                      required
                    />
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/50 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadDocModal(false);
                      setUploadDocForm({
                        file: null,
                        type: 'new',
                        reason: '',
                        changes: '',
                        updated_from: ''
                      });
                      setError('');
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadDocument}
                    disabled={uploadingDocuments || !uploadDocForm.file || !uploadDocForm.reason}
                    className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploadingDocuments ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Mengupload...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-upload mr-2"></i>
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Comments Panel */}
      {showReviewComments && (
        <div className={`fixed right-0 top-14 bottom-0 w-96 shadow-2xl z-40 overflow-y-auto transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-l border-gray-700' : 'bg-white border-l border-gray-200'
          }`}>
          <div className={`sticky top-0 z-10 p-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Review Comments
              </h3>
              <button
                onClick={() => setShowReviewComments(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                  }`}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* New Comment Form */}
            <form onSubmit={handleCreateReviewComment} className="space-y-2">
              <textarea
                value={newReviewComment}
                onChange={(e) => setNewReviewComment(e.target.value)}
                placeholder="Add a review comment..."
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 ${darkMode
                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
              <button
                type="submit"
                disabled={!newReviewComment.trim() || submittingReviewComment}
                className="w-full px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {submittingReviewComment ? 'Adding...' : 'Add Comment'}
              </button>
            </form>
          </div>

          {/* Comments List */}
          <div className="p-4 space-y-4">
            {loadingReviewComments ? (
              <div className="text-center py-8">
                <i className={`fa-solid fa-spinner fa-spin text-2xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}></i>
                <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading comments...</p>
              </div>
            ) : reviewComments.length === 0 ? (
              <div className="text-center py-8">
                <i className={`fa-solid fa-comments text-4xl ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}></i>
                <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No review comments yet
                </p>
              </div>
            ) : (
              reviewComments.map((comment) => (
                <div
                  key={comment.id}
                  onClick={() => navigate(`/agreement/${id}/review`)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${comment.is_resolved
                    ? darkMode
                      ? 'bg-green-900/20 border-green-800 hover:bg-green-900/30'
                      : 'bg-green-50 border-green-200 hover:bg-green-100'
                    : darkMode
                      ? 'bg-gray-900 border-gray-700 hover:bg-gray-800'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                >
                  {/* Comment Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${darkMode ? 'bg-violet-900 text-violet-200' : 'bg-violet-100 text-violet-700'
                        }`}>
                        {(comment.user?.name || comment.created_by?.name)?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {comment.user?.name || comment.created_by?.name || 'Unknown'}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatRelativeTime(comment.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={comment.is_resolved || false}
                          onChange={() =>
                            comment.is_resolved
                              ? handleUnflagReviewCommentAsDone(comment.id)
                              : handleFlagReviewCommentAsDone(comment.id)
                          }
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Done
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Comment Content */}
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {comment.content}
                  </p>

                  {/* Reply Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setReplyingToCommentId(replyingToCommentId === comment.id ? null : comment.id);
                    }}
                    className={`text-xs font-medium ${darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
                      }`}
                  >
                    <i className="fa-solid fa-reply mr-1"></i>
                    Reply
                  </button>

                  {/* Reply Form */}
                  {replyingToCommentId === comment.id && (
                    <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write a reply..."
                        rows={2}
                        className={`w-full px-3 py-2 border rounded-lg resize-none text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${darkMode
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                          : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleReplyToReviewComment(comment.id)}
                          disabled={!replyContent.trim() || submittingReply}
                          className="px-3 py-1 bg-violet-600 text-white rounded text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                        >
                          {submittingReply ? 'Sending...' : 'Send'}
                        </button>
                        <button
                          onClick={() => {
                            setReplyingToCommentId(null);
                            setReplyContent('');
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium ${darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-3 space-y-2 pl-4 border-l-2 border-violet-500">
                      {comment.replies.map((reply: any) => (
                        <div key={reply.id} className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <div className="flex items-center space-x-2 mb-1">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${darkMode ? 'bg-violet-900 text-violet-200' : 'bg-violet-100 text-violet-700'
                              }`}>
                              {(reply.user?.name || reply.created_by?.name)?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {reply.user?.name || reply.created_by?.name || 'Unknown'}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {formatRelativeTime(reply.created_at)}
                            </p>
                          </div>
                          <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {reply.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {/* Compare with AI Modal */}
      <AICompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        taskId={parseInt(id!)}
        darkMode={darkMode}
      />
      <AssignModal
        isOpen={showReviewAssignModal}
        onClose={() => setShowReviewAssignModal(false)}
        onAssign={handleAssignReviewer}
        users={users}
        title="Submit to Review & Assign"
        isSubmitting={isAssigning}
      />
      <HistorySidebar
        taskId={parseInt(id!)}
        logs={logs}
        assignments={assignments}
        versions={documentVersions}
        isOpen={historyOpen}
        onToggle={() => setHistoryOpen(!historyOpen)}
        loading={loading}
        statuses={statuses}
      />
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowUploadModal(false)}></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'
                  }`}>
                  <i className={`fa-solid fa-upload ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} id="modal-title">
                    Upload New Version
                  </h3>
                  <div className="mt-4 space-y-4">
                    {/* File Input */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Document File (.docx) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept=".docx"
                        className={`block w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            ${darkMode
                            ? 'file:bg-indigo-900 file:text-indigo-300 hover:file:bg-indigo-800'
                            : 'file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100'
                          }
                          `}
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            setUploadVersionFile(e.target.files[0]);
                          }
                        }}
                      />
                      {uploadVersionFile && (
                        <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Selected: {uploadVersionFile.name}
                        </p>
                      )}
                    </div>

                    {/* Note Input */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Catatan <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={3}
                        className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border rounded-md p-2 ${darkMode
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'border-gray-300 text-gray-900'
                          }`}
                        placeholder="Masukkan catatan tentang perubahan versi ini..."
                        value={uploadNote}
                        onChange={(e) => setUploadNote(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <button
                type="button"
                disabled={uploadingVersion || !uploadVersionFile || !uploadNote.trim()}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                onClick={handleUploadVersion}
              >
                {uploadingVersion ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
              <button
                type="button"
                className={`mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm ${darkMode
                  ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300'
                  }`}
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <HistorySidebar
        taskId={Number(id)}
        task={task}
        logs={logs}
        assignments={assignments}
        versions={documentVersions}
        reviewComments={reviewComments}
        isOpen={historyOpen}
        onToggle={() => {
          setHistoryOpen(!historyOpen);
          if (!historyOpen) setShowReviewComments(false);
        }}
        loading={loading}
        statuses={statuses}
      />
    </div >
  );
};

export default TaskDetail;
